// DocumentEditorView.jsx - REFACTORED FOR SEPARATE CODIFICATIONS COLLECTION
// Major changes:
// 1. Replaced codifiedContents state with codifications API calls
// 2. Load codifications from GET /api/codifications?documentId={id}
// 3. Save codifications via POST/PUT to /api/codifications
// 4. Removed embedded codifiedContents from document save payload
// 5. Added feature flag for gradual migration (STRANGLER FIG pattern)

import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box, Heading, HStack, VStack, IconButton, Image, Tooltip, useToast, Input,
  Slider, SliderTrack, SliderFilledTrack, SliderThumb, Text, Select,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Button,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  Menu, MenuButton, MenuList, MenuItem,
} from "@chakra-ui/react";
import { SmallCloseIcon } from "@chakra-ui/icons";
import { useEditor, EditorContent, BubbleMenu } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Table from "@tiptap/extension-table";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import { Extension } from "@tiptap/core";
import Paragraph from "@tiptap/extension-paragraph";
import { Plugin, PluginKey, TextSelection } from "prosemirror-state";
import { TableMap } from "prosemirror-tables";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCursor from "@tiptap/extension-collaboration-cursor";
import * as Y from "yjs";
import { HocuspocusProvider } from "@hocuspocus/provider";
import FontSize from "../extensions/FontSize";
import Navbar from "../Navbar";
import HeroImage from "../images/stamps.png";
import { CustomTableRow } from "../extensions/CustomTableRow";
import { CustomCell } from "../extensions/CustomCell";
import { CustomTableHeader } from "../extensions/CustomTableHeader";
import MenuBar from "./MenuBar";
import PlacingProcessBar from "./PlacingProcessBar";
import QueueMonitor from "./QueueMonitor";
import {
  HiOutlineArrowNarrowLeft, HiOutlineSave, HiOutlinePlus, HiOutlineMinus, HiOutlineTrash, HiOutlineLink, HiOutlineColorSwatch, HiOutlinePrinter,
} from "react-icons/hi";
import { MdFormatColorText, MdFormatColorReset } from "react-icons/md";
import { PageBreak } from "./PageBreak";
import AutoResizeTextarea from "../AutoResizeTextarea";
import CodifyQueue from "../utils/CodifyQueue";
import ReactJson from "react-json-view";
import { useAuth } from "../AuthContext";
import DocumentMetadataEditor from "./DocumentMetadataEditor";
import CodesetSelect from "../CodesetSelect";

const DOCS_API = "https://documents.poc.mrc.ai/api/documents";
const CODIFY_API = "https://codify.poc.mrc.ai/api/codifyv3";
const CODIFICATIONS_API = "https://documents.poc.mrc.ai/api/codifications";
const CODIFY_SPECIFICATIONS_API = "https://codify.poc.mrc.ai/api/specifications";

function safeUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "uuid-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function generateUserColor(name) {
  return '#718096';
}

const LockManagement = Extension.create({
  name: 'lockManagement',

  addProseMirrorPlugins() {
    const userRoles = this.options.userRoles || [];
    const debugMode = this.options.debugMode || false;

    if (debugMode) console.log('🔒 LockManagement: Initializing with userRoles:', userRoles);

    const applyLocks = (state) => {
      let tr = state.tr;
      let changed = false;

      state.doc.descendants((node, pos) => {
        if (node.type.name === 'tableRow') {
          const editableBy = node.attrs?.editableBy;

          if (debugMode && editableBy && editableBy.length > 0) {
            if (debugMode) console.log('🔒 Found row with editableBy:', editableBy, 'at pos:', pos);
          }

          let cellOffset = 1;
          node.forEach((cell) => {
            const cellPos = pos + cellOffset;
            const cellRole = cell.attrs?.role;

            if (cellRole === 'heading' || cellRole === 'content') {
              const canEdit = editableBy && editableBy.length > 0 &&
                userRoles.some(ur => {
                  const userRoleCode = ur.role || ur;
                  const matches = editableBy.some(eb => {
                    const match = eb.toLowerCase() === userRoleCode.toLowerCase();
                    return match;
                  });
                  return matches;
                });

              if (debugMode) {
                if (debugMode) console.log(`🔒 Cell at ${cellPos}, role: ${cellRole}, editableBy: ${JSON.stringify(editableBy)}, userRoles: ${JSON.stringify(userRoles)}, canEdit: ${canEdit}`);
              }

              const currentClass = cell.attrs?.class || '';
              const shouldHaveLock = !canEdit;
              const hasLock = currentClass.includes('locked');

              if (shouldHaveLock && !hasLock) {
                if (debugMode) console.log(`🔒 Adding lock to cell at ${cellPos}`);
                tr.setNodeMarkup(cellPos, undefined, {
                  ...cell.attrs,
                  class: currentClass ? `${currentClass} locked` : 'locked'
                });
                changed = true;
              } else if (!shouldHaveLock && hasLock) {
                if (debugMode) console.log(`🔒 Removing lock from cell at ${cellPos}`);
                tr.setNodeMarkup(cellPos, undefined, {
                  ...cell.attrs,
                  class: currentClass.replace(/\s*locked\s*/g, '').trim()
                });
                changed = true;
              }
            }

            cellOffset += cell.nodeSize;
          });
        }
        return true;
      });

      return { tr, changed };
    };

    return [
      new Plugin({
        key: new PluginKey('lockManagement'),

        view(editorView) {
          if (debugMode) console.log('🔒 LockManagement view created');
          setTimeout(() => {
            const { tr, changed } = applyLocks(editorView.state);
            if (changed) {
              if (debugMode) console.log('🔒 Applying initial locks via view');
              editorView.dispatch(tr);
            }
          }, 10);
          return {};
        },

        appendTransaction(_txs, _old, state) {
          const { tr, changed } = applyLocks(state);
          return changed ? tr : null;
        },
        
        filterTransaction(tr, state) {
          if (!tr.docChanged) return true;

          let allowed = true;

          tr.steps.forEach((step, i) => {
            if (!allowed || !step.slice) return;

            const $from = tr.docs[i].resolve(step.from);

            let cellNode = null;
            let rowNode = null;

            for (let d = $from.depth; d > 0; d--) {
              const node = $from.node(d);
              if (!cellNode && (node.type.name === 'tableCell' || node.type.name === 'tableHeader')) {
                cellNode = node;
              }
              if (!rowNode && node.type.name === 'tableRow') {
                rowNode = node;
              }
              if (cellNode && rowNode) break;
            }

            if (cellNode && rowNode) {
              const cellRole = cellNode.attrs?.role;
              const editableBy = rowNode.attrs?.editableBy;

              if (cellRole === 'heading' || cellRole === 'content') {
                const canEdit = editableBy && editableBy.length > 0 &&
                  userRoles.some(ur => {
                    const userRoleCode = ur.role || ur;
                    return editableBy.some(eb =>
                      eb.toLowerCase() === userRoleCode.toLowerCase()
                    );
                  });

                if (!canEdit) {
                  allowed = false;
                }
              }
            }
          });

          return allowed;
        },
      }),
    ];
  },
});

function findTableFromSelection(selection) {
  for (let d = selection.$from.depth; d > 0; d--) {
    const node = selection.$from.node(d);
    if (node.type?.spec?.tableRole === "table") {
      return { node, pos: selection.$from.before(d) };
    }
  }
  return null;
}

function findCellFromSelection(selection) {
  for (let d = selection.$from.depth; d > 0; d--) {
    const node = selection.$from.node(d);
    const role = node.type?.spec?.tableRole;
    if (role === "cell" || role === "header_cell") {
      return { node, pos: selection.$from.before(d) };
    }
  }
  return null;
}

const CustomTable = Table.extend({
  addExtensions() {
    return [CustomTableRow, CustomCell, CustomTableHeader];
  },
  addKeyboardShortcuts() {
    const parent = this.parent?.() ?? {};
    return {
      ...parent,
      Tab: ({ editor }) => {
        const { state } = editor;
        const tableInfo = findTableFromSelection(state.selection);
        if (!tableInfo) return parent.Tab?.({ editor }) ?? false;

        const cellInfo = findCellFromSelection(state.selection);
        if (!cellInfo) return parent.Tab?.({ editor }) ?? false;

        const map = TableMap.get(tableInfo.node);
        const tableStart = tableInfo.pos + 1;
        const rect = map.findCell(cellInfo.pos - tableStart);
        const isLastCell = rect.left === map.width - 1 && rect.top === map.height - 1;

        if (!isLastCell) return parent.Tab?.({ editor }) ?? false;

        const after = tableInfo.pos + tableInfo.node.nodeSize;
        let tr = state.tr;
        const nextNode = state.doc.nodeAt(after);
        if (!nextNode) {
          const para = state.schema.nodes.paragraph;
          if (para) tr = tr.insert(after, para.createAndFill());
        }
        const selPos = Math.min(after + 1, tr.doc.content.size);
        tr = tr.setSelection(TextSelection.create(tr.doc, selPos)).scrollIntoView();
        editor.view.dispatch(tr);
        return true;
      },
      ArrowUp: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;

        const $pos = selection.$from;
        const cellInfo = findCellFromSelection(selection);
        if (!cellInfo) return parent.ArrowUp?.({ editor }) ?? false;

        const cellStart = cellInfo.pos + 1;
        if ($pos.pos !== cellStart) return parent.ArrowUp?.({ editor }) ?? false;

        const result = parent.ArrowUp?.({ editor }) ?? false;

        setTimeout(() => {
          const newState = editor.state;
          const newSelection = newState.selection;
          const newCellInfo = findCellFromSelection(newSelection);

          if (newCellInfo) {
            const cellEnd = newCellInfo.pos + newCellInfo.node.nodeSize - 1;
            const tr = newState.tr.setSelection(TextSelection.create(newState.doc, cellEnd));
            editor.view.dispatch(tr);
          }
        }, 0);

        return result;
      },
      "Mod-a": ({ editor }) => {
        const { state } = editor;
        const cellInfo = findCellFromSelection(state.selection);

        if (!cellInfo) return parent["Mod-a"]?.({ editor }) ?? false;

        const cellStart = cellInfo.pos + 1;
        const cellEnd = cellInfo.pos + cellInfo.node.nodeSize - 1;

        const tr = state.tr.setSelection(TextSelection.create(state.doc, cellStart, cellEnd));
        editor.view.dispatch(tr);

        return true;
      },
    };
  },
});

const StableRowIds = Extension.create({
  name: "stableRowIds",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction(_txs, _old, state) {
          let tr = state.tr;
          let changed = false;
          state.doc.descendants((node, pos) => {
            if (node.type.name === "tableRow" && !node.attrs?.id) {
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, id: safeUuid() });
              changed = true;
            }
            return true;
          });
          return changed ? tr : null;
        },
      }),
    ];
  },
});

export default function DocumentEditorView({
  tiptapDocument,
  contract,
  documentMeta,
  onClose,
  onSave,
  userRoles = [],
}) {
  const toast = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.platformUser?.isSuperAdmin === "true";
  const [debugMode, setDebugMode] = useState(false);

  const TEST_LOCK_MODE = false;
  const effectiveUserRoles = TEST_LOCK_MODE
    ? userRoles.filter(r => r.role !== 'broker')
    : userRoles;

  if (debugMode) {
    if (debugMode) console.log("📋 User roles in Document Editor:", userRoles);
    if (TEST_LOCK_MODE) {
      if (debugMode) console.log("⚠️ TEST MODE ACTIVE: Simulating user without broker role:", effectiveUserRoles);
    }
  }

  const queueRef = useRef(null);
  if (!queueRef.current) {
    queueRef.current = new CodifyQueue({ concurrency: 1, maxRetries: 2, baseDelayMs: 700, jitterMs: 300 });
  }

  const ydocRef = useRef(null);
  const providerRef = useRef(null);
  const yCodifiedContentsRef = useRef(null);

  if (!ydocRef.current) {
    ydocRef.current = new Y.Doc({ gc: true, gcFilter: () => true });
    yCodifiedContentsRef.current = ydocRef.current.getMap('codifiedContents');
  }

  if (!providerRef.current && documentMeta?.documentRepositoryId) {
    const provider = new HocuspocusProvider({
      url: 'wss://studio.poc.mrc.ai/collab',
      name: documentMeta.documentRepositoryId,
      document: ydocRef.current,
      connect: true,

      onConnect: () => {
        if (debugMode) {
          if (debugMode) console.log('🔗 Hocuspocus: Connected to collaboration server');
        }
      },

      onDisconnect: ({ event }) => {
        if (debugMode) {
          if (debugMode) console.log('🔌 Hocuspocus: Disconnected from server', event);
        }
      },

      onStatus: ({ status }) => {
        if (debugMode) {
          if (debugMode) console.log('📡 Hocuspocus status:', status);
        }
      },

      onSynced: ({ state }) => {
        if (debugMode) {
          if (debugMode) console.log('🔄 Hocuspocus: Synced', state);
        }
      },

      onClose: ({ event }) => {
        if (debugMode) {
          if (debugMode) console.log('🔌 Hocuspocus: Connection closed', event);
        }
        if (event.code !== 1000) {
          toast({
            title: 'Collaboration Disconnected',
            description: 'Attempting to reconnect...',
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
        }
      },

      preserveConnection: true,
      quiet: !debugMode,
    });

    providerRef.current = provider;
  }

  // State for ALL codifications - maps participantRole -> codification
  const [codificationsByRole, setCodificationsByRole] = useState(new Map());
  const [selectedParticipantRole, setSelectedParticipantRole] = useState("Broker");

  // rowData map for CURRENT participant - maps rowId -> row codification data
  const [rowDataMap, setRowDataMap] = useState(new Map());
  const rowDataMapRef = useRef(rowDataMap);
  useEffect(() => { rowDataMapRef.current = rowDataMap; }, [rowDataMap]);

  // Helper to get current codification
  const currentCodification = codificationsByRole.get(selectedParticipantRole);
  const codificationId = currentCodification?._id || null;

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [zoom, setZoom] = useState(1.25);
  const [jsonZoom, setJsonZoom] = useState(1.0);
  const [panelWidth, setPanelWidth] = useState(22);
  const [isDragging, setIsDragging] = useState(false);
  const [expandedAccordionIndices, setExpandedAccordionIndices] = useState([]);

  const [isMetadataEditorOpen, setIsMetadataEditorOpen] = useState(false);
  const [selectedRowMetadata, setSelectedRowMetadata] = useState(null);

  const [placingStage, setPlacingStage] = useState("build");

  // Auto-save state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  // BubbleMenu state
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [placingStepCode, setPlacingStepCode] = useState("submission");
  const [participantRole, setParticipantRole] = useState("Broker");

  const [editableDocMeta, setEditableDocMeta] = useState({
    name: documentMeta?.name || "Untitled Document",
    codifyCollection: documentMeta?.codifyCollection || null,
    dataStandard: documentMeta?.dataStandard || null,
    dataType: documentMeta?.dataType || null,
    model: documentMeta?.model || null,
  });

  const [codifySpecifications, setCodifySpecifications] = useState(documentMeta?.codifySpecifications || []);
  const [availableSpecifications, setAvailableSpecifications] = useState([]);
  const [loadingSpecifications, setLoadingSpecifications] = useState(false);
  const [selectedSpecificationId, setSelectedSpecificationId] = useState(''); // For controlling the dropdown
  const [specificationCodifications, setSpecificationCodifications] = useState(new Map()); // Map of specId -> codified data
  const [specificationCodificationIds, setSpecificationCodificationIds] = useState(new Map()); // Map of specId -> codification _id
  const [coreDefinitions, setCoreDefinitions] = useState([]);
  const [specificationDefinitions, setSpecificationDefinitions] = useState(new Map()); // Map of specId -> definitions array
  
  // Refs to access latest definitions synchronously (avoid race conditions)
  const coreDefinitionsRef = useRef([]);
  const specificationDefinitionsRef = useRef(new Map());
  const codifySpecificationsRef = useRef([]);
  const specificationCodificationsRef = useRef(new Map());

  useEffect(() => { coreDefinitionsRef.current = coreDefinitions; }, [coreDefinitions]);
  useEffect(() => { specificationDefinitionsRef.current = specificationDefinitions; }, [specificationDefinitions]);
  useEffect(() => { codifySpecificationsRef.current = codifySpecifications; }, [codifySpecifications]);
  useEffect(() => { specificationCodificationsRef.current = specificationCodifications; }, [specificationCodifications]);

  useEffect(() => {
    const fetchSpecifications = async () => {
      setLoadingSpecifications(true);
      try {
        const res = await fetch(CODIFY_SPECIFICATIONS_API);
        if (res.ok) {
          const specs = await res.json();
          setAvailableSpecifications(specs);
        }
      } catch (err) {
        console.error('Failed to fetch codify specifications:', err);
      } finally {
        setLoadingSpecifications(false);
      }
    };
    fetchSpecifications();
  }, []);

  // Fetch core definitions when codifyCollection changes
  useEffect(() => {
    const fetchCoreDefinitions = async () => {
      const collection = editableDocMeta.codifyCollection;
      if (!collection) {
        setCoreDefinitions([]);
        return;
      }

      try {
        const res = await fetch(`https://codify.poc.mrc.ai/api/definitions?collection=${collection}`);
        if (res.ok) {
          const defs = await res.json();
          setCoreDefinitions(Array.isArray(defs) ? defs : []);
          if (debugMode) console.log('[CORE DEFS] Loaded definitions for collection:', collection, defs);
        }
      } catch (err) {
        console.error('Failed to fetch core definitions:', err);
        setCoreDefinitions([]);
      }
    };
    fetchCoreDefinitions();
  }, [editableDocMeta.codifyCollection]);

  // Fetch definitions for each specification
  // This only runs on initial load when specs are loaded from saved document
  useEffect(() => {
    const fetchSpecDefinitions = async () => {
      // Start with existing definitions to preserve manually-set ones
      setSpecificationDefinitions(prev => {
        const newDefsMap = new Map(prev);

        // Only fetch for specs that DON'T have definitions yet
        const specsNeedingDefs = codifySpecifications.filter(spec => {
          const specId = spec.specificationId || spec._id || spec.id;
          return !newDefsMap.has(specId);
        });

        if (specsNeedingDefs.length === 0) {
          console.log('[SPEC DEFS] All specs already have definitions, skipping fetch');
          return prev;
        }

        // Fetch definitions for specs that need them
        console.log('[SPEC DEFS] Fetching definitions for', specsNeedingDefs.length, 'specs');

        (async () => {
          for (const spec of specsNeedingDefs) {
            const specId = spec.specificationId || spec._id || spec.id;
            if (!spec.codifyCollection) continue;

            try {
              const res = await fetch(`https://codify.poc.mrc.ai/api/definitions?collection=${spec.codifyCollection}`);
              if (res.ok) {
                const defs = await res.json();
                setSpecificationDefinitions(prevMap => {
                  const updated = new Map(prevMap);
                  updated.set(specId, Array.isArray(defs) ? defs : []);
                  console.log('[SPEC DEFS] Loaded definitions for specification:', spec.name, 'count:', Array.isArray(defs) ? defs.length : 0);
                  return updated;
                });
              }
            } catch (err) {
              console.error(`Failed to fetch definitions for specification ${spec.name}:`, err);
            }
          }
        })();

        return prev;
      });
    };

    if (codifySpecifications.length > 0) {
      fetchSpecDefinitions();
    } else {
      setSpecificationDefinitions(new Map());
    }
  }, [codifySpecifications]);

  useEffect(() => {
    const newEditableMeta = {
      name: documentMeta?.name || "Untitled Document",
      codifyCollection: documentMeta?.codifyCollection || null,
      dataStandard: documentMeta?.dataStandard || null,
      dataType: documentMeta?.dataType || null,
      model: documentMeta?.model || null,
    };

    if (debugMode) {
      if (debugMode) console.log("[DocumentEditorView] Updating editableDocMeta from documentMeta:", {
        documentMetaName: documentMeta?.name,
        hasName: !!documentMeta?.name,
        nameValue: JSON.stringify(documentMeta?.name),
        newEditableMetaName: newEditableMeta.name,
        codifySpecifications: documentMeta?.codifySpecifications,
      });
    }

    setEditableDocMeta(newEditableMeta);

    // Normalize specs loaded from database to ensure they have specificationId field
    const normalizedSpecs = (documentMeta?.codifySpecifications || []).map(spec => ({
      ...spec,
      specificationId: spec.specificationId || spec._id || spec.id,
    }));

    setCodifySpecifications(normalizedSpecs);

    if (debugMode) {
      if (debugMode) console.log("[CODIFY SPECS] Loading from documentMeta:", documentMeta?.codifySpecifications);
      if (debugMode) console.log("[CODIFY SPECS] Normalized specs:", normalizedSpecs);
    }
  }, [documentMeta, debugMode]);

  useEffect(() => {
    const currentProvider = providerRef.current;

    if (debugMode && currentProvider) {
      if (debugMode) console.log('🔗 Hocuspocus: Connected to room:', documentMeta?.documentRepositoryId);
    }

    return () => {
      if (currentProvider) {
        if (debugMode) {
          if (debugMode) console.log('🔗 Hocuspocus: Disconnecting from room');
        }
        currentProvider.destroy();
        providerRef.current = null;
      }
    };
  }, [documentMeta?.documentRepositoryId, debugMode]);

  const getStageDisplayName = (stage) => {
    switch (stage) {
      case "build": return "Build";
      case "quotation": return "Quotation";
      case "order": return "Order";
      case "bound": return "Bound";
      default: return "Build";
    }
  };

  const parsedDoc = React.useMemo(() => {
    if (!tiptapDocument) return null;
    try {
      return typeof tiptapDocument === "string" ? JSON.parse(tiptapDocument) : tiptapDocument;
    } catch (e) {
      if (debugMode) console.error("Failed to parse tiptapDocument", e);
      toast({ title: "Error", description: "Invalid document format.", status: "error", duration: 4000, isClosable: true });
      return null;
    }
  }, [tiptapDocument, toast]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: false,
        history: false, // Disabled - Collaboration extension provides its own history (undo/redo)
        blockquote: {
          HTMLAttributes: {
            class: 'blockquote-content',
          },
        },
      }),
      Paragraph.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            textIndent: {
              default: null,
              parseHTML: element => element.style.textIndent || null,
              renderHTML: attributes => {
                if (!attributes.textIndent) {
                  return {};
                }
                return {
                  style: `text-indent: ${attributes.textIndent}`,
                };
              },
            },
          };
        },
      }),
      PageBreak,
      TextStyle.extend({
        addAttributes() {
          return {
            fontSize: {
              default: "1rem",
              parseHTML: el => el.style.fontSize?.replace(/['"]/g, ""),
              renderHTML: attrs => (attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}),
            },
            lineHeight: {
              default: "normal",
              parseHTML: el => el.style.lineHeight?.replace(/['"]/g, ""),
              renderHTML: attrs => (attrs.lineHeight ? { style: `line-height: ${attrs.lineHeight}` } : {}),
            },
          };
        },
      }),
      FontSize,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
      CustomTable.configure({ resizable: true, withHeaderRow: true, handleWidth: 6, allowTableEditing: true }),
      StableRowIds,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'custom-link',
        },
      }),
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      LockManagement.configure({
        userRoles: effectiveUserRoles || [],
        debugMode: debugMode,
      }),
      Collaboration.configure({
        document: ydocRef.current,
      }),
      CollaborationCursor.configure({
        provider: providerRef.current,
        user: {
          name: user?.name || user?.email || 'Anonymous',
          color: generateUserColor(user?.name || user?.email || 'Anonymous'),
        },
      }),
    ],
    editable: true,
  });

  const [cellInfo, setCellInfo] = useState({ current: null, previous: null });
  const [documentLoaded, setDocumentLoaded] = useState(false);

  useEffect(() => {
    if (!editor || !providerRef.current || !parsedDoc) return;

    const provider = providerRef.current;

    const handleSynced = ({ state }) => {
      if (state && ydocRef.current) {
        const fragment = ydocRef.current.getXmlFragment('default');

        if (fragment.length === 0) {
          if (debugMode) {
            if (debugMode) console.log('🔄 Yjs document empty, loading initial content');
          }
          editor.commands.setContent(parsedDoc);
        } else {
          if (debugMode) {
            if (debugMode) console.log('🔄 Yjs document has content, skipping initial load');
          }
        }
        if (debugMode) console.log('🔄 Document loaded, triggering codification rebuild');
        setDocumentLoaded(true);
      }
    };

    if (provider.synced) {
      handleSynced({ state: true });
    } else {
      provider.on('synced', handleSynced);
    }

    return () => {
      provider.off('synced', handleSynced);
    };
  }, [editor, parsedDoc, debugMode]);

  useEffect(() => {
    if (!editor) return;
    const tr = editor.view.state.tr.setMeta("stampRowIds", true);
    editor.view.dispatch(tr);
  }, [editor]);

  // Load ALL codifications from API on mount (for all participants)
  useEffect(() => {
    if (debugMode) console.log('[CODIFICATION API] useEffect triggered:', {
      hasDocumentRepositoryId: !!documentMeta?.documentRepositoryId,
      codifySpecificationsLength: codifySpecifications.length,
      codifySpecifications: codifySpecifications,
    });

    if (!documentMeta?.documentRepositoryId) {
      if (debugMode) console.log('[CODIFICATION API] Skipping: No documentRepositoryId');
      return;
    }

    const loadCodifications = async () => {
      try {
        console.log('[CODIFICATION API] ========================================');
        console.log('[CODIFICATION API] Loading codifications for specifications:', codifySpecifications.length);
        console.log('[CODIFICATION API] Full specs:', codifySpecifications);

        // Preserve existing manually-initialized codifications (specs without codificationId yet)
        console.log('[CODIFICATION API] Preserving existing codifications before loading from API');
        console.log('[CODIFICATION API] Current specificationCodifications size:', specificationCodifications.size);
        const specCodifMap = new Map(specificationCodifications); // Start with existing codifications
        const specCodifIdMap = new Map(specificationCodificationIds); // Start with existing IDs

        // Load each specification's codification if it has a codificationId
        for (const spec of codifySpecifications) {
          // Normalize specificationId (support id, _id, and specificationId fields)
          const specId = spec.specificationId || spec._id || spec.id;

          if (!spec.codificationId) {
            console.log('[CODIFICATION API] ⏭️ Spec has no codificationId yet (newly added, not saved):', spec.name);
            continue;
          }

          console.log('[CODIFICATION API] 📥 Loading codification for spec:', spec.name, 'specId:', specId, 'codificationId:', spec.codificationId);

          try {
            const url = `${CODIFICATIONS_API}/${spec.codificationId}`;
            if (debugMode) console.log('[CODIFICATION API] Fetching from URL:', url);

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

            const response = await fetch(url, { signal: controller.signal });
            clearTimeout(timeoutId);

            if (debugMode) console.log('[CODIFICATION API] Response received:', response.status, response.statusText);

            if (!response.ok) {
              console.warn('[CODIFICATION API] Failed to load codification for spec:', spec.name, spec.codificationId, response.statusText);
              continue;
            }

            const codif = await response.json();
            console.log('[CODIFICATION API] ✅ Codification loaded successfully:', {
              specName: spec.name,
              specId: specId,
              codifId: codif._id,
              rowsCount: codif.rows?.length || 0,
              sampleRows: codif.rows?.slice(0, 2)
            });

            // Store the codification ID for this specification
            specCodifIdMap.set(specId, codif._id);

            // Build rowData map for this specification
            const specRowMap = new Map();
            if (codif.rows && Array.isArray(codif.rows)) {
              codif.rows.forEach(row => {
                specRowMap.set(row.id, {
                  rowId: row.id,
                  state: row.state || 'codified',
                  inputText: row.content || '',
                  output: row.data || '',
                  headingType: row.type || row.heading || 'Unknown',
                });
              });
            }
            console.log('[CODIFICATION API] Built row map for spec:', spec.name, 'with', specRowMap.size, 'rows');
            specCodifMap.set(specId, specRowMap);

            if (debugMode) console.log('[CODIFICATION API] Storing in Map with key:', specId, 'for spec:', spec.name, 'with', specRowMap.size, 'entries');
            if (debugMode) {
              if (debugMode) console.log('[CODIFICATION API] Built specRowMap for', spec.name, 'with', specRowMap.size, 'entries');
            }
          } catch (error) {
            if (error.name === 'AbortError') {
              console.error('[CODIFICATION API] Timeout loading codification for spec:', spec.name, spec.codificationId);
            } else {
              console.error('[CODIFICATION API] Error loading codification for spec:', spec.name, error);
            }
          }
        }
        
        setSpecificationCodifications(specCodifMap);
        setSpecificationCodificationIds(specCodifIdMap);

        if (debugMode) console.log('[CODIFICATION API] Final state:', {
          specCodifMapSize: specCodifMap.size,
          specCodifIdMapSize: specCodifIdMap.size,
          specIds: Array.from(specCodifMap.keys()),
          fullMap: specCodifMap,
        });
        if (debugMode) {
          if (debugMode) console.log('[CODIFICATION API] Detailed final state');
        }
      } catch (error) {
        console.error('[CODIFICATION API] Error loading codifications:', error);
        toast({
          title: 'Failed to Load Codifications',
          description: error.message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    };

    loadCodifications();
  }, [documentMeta?.documentRepositoryId, codifySpecifications, debugMode, toast]);

  // Sync rowDataMap changes to Yjs for collaboration
  useEffect(() => {
    if (!yCodifiedContentsRef.current) return;

    const yMap = yCodifiedContentsRef.current;

    const handleYChange = () => {
      const updates = {};
      yMap.forEach((value, key) => {
        updates[key] = value;
      });

      setRowDataMap(prev => {
        const newMap = new Map(prev);
        Object.entries(updates).forEach(([rowId, remoteData]) => {
          const local = newMap.get(rowId);
          if (!local || remoteData.version > (local.version || 0)) {
            if (debugMode) {
              if (debugMode) console.log('📥 Received codification update from remote:', rowId, remoteData.state);
            }
            newMap.set(rowId, remoteData);
          }
        });
        return newMap;
      });
    };

    yMap.observe(handleYChange);

    return () => {
      yMap.unobserve(handleYChange);
    };
  }, [debugMode]);

  // Push rowDataMap changes to Yjs
  useEffect(() => {
    if (!yCodifiedContentsRef.current || rowDataMap.size === 0) return;

    const yMap = yCodifiedContentsRef.current;

    rowDataMap.forEach((rowData, rowId) => {
      const existingEntry = yMap.get(rowId);
      const newVersion = (existingEntry?.version || 0) + 1;

      if (!existingEntry ||
          existingEntry.state !== rowData.state ||
          existingEntry.data !== rowData.data) {

        if (debugMode) {
          if (debugMode) console.log('📤 Pushing codification update to remote:', rowId, rowData.state);
        }

        yMap.set(rowId, {
          ...rowData,
          version: newVersion,
          timestamp: Date.now(),
        });
      }
    });
  }, [rowDataMap, debugMode]);


  // Listen for document updates to detect new rows
  useEffect(() => {
    if (!editor) return;

    let updateTimer = null;

    const handleUpdate = () => {
      if (updateTimer) {
        clearTimeout(updateTimer);
      }

      updateTimer = setTimeout(() => {
        // Build rowDataMap for new rows
        const currentRowIds = new Set();
        editor.state.doc.descendants((node) => {
          if (node.type.name === "tableRow") {
            currentRowIds.add(node.attrs?.id);
          }
          return true;
        });

        const existingIds = new Set(rowDataMapRef.current.keys());
        const hasNewRows = Array.from(currentRowIds).some(id => !existingIds.has(id));

        if (hasNewRows) {
          if (debugMode) console.log('[NEW ROW] Detected new rows, updating rowDataMap');

          setRowDataMap(prevMap => {
            const newMap = new Map(prevMap);
            let rowCounter = 0;

            editor.state.doc.descendants((node) => {
              if (node.type.name !== "tableRow") return true;
              const rowId = node.attrs?.id || safeUuid();
              const headingType = node.attrs?.headingType || "Unknown";

              if (!newMap.has(rowId)) {
                // New row - create entry with all metadata
                if (debugMode) console.log('[NEW ROW] Creating entry for new row:', rowId, 'type:', headingType);

                // Extract content from the content cell (second cell)
                let contentText = "";
                let colIndex = 0;
                node.forEach((cellNode) => {
                  colIndex += 1;
                  if (cellNode.attrs?.role === "content" || colIndex === 2) {
                    contentText = cellNode.textContent?.trim() || "";
                  }
                });

                newMap.set(rowId, {
                  id: rowId,
                  row: rowCounter,
                  col: 1,
                  segment: "tableRow",
                  type: "content",
                  heading: headingType,
                  content: contentText,
                  data: null,
                  state: "new",
                });
              }
              rowCounter += 1;
              return true;
            });

            return newMap;
          });
        }
      }, 500);
    };

    editor.on("update", handleUpdate);
    return () => {
      if (updateTimer) clearTimeout(updateTimer);
      editor.off("update", handleUpdate);
    };
  }, [editor, contract?.productCode, debugMode]);

  // Helper to update codifyStatus attribute on table rows
  const updateRowStatus = useCallback((rowId, status) => {
    if (!editor) {
      if (debugMode) console.log('[UPDATE STATUS] No editor, skipping');
      return;
    }

    const { state } = editor;
    let tr = state.tr;
    let updated = false;
    let found = false;

    state.doc.descendants((node, pos) => {
      if (node.type.name === "tableRow" && node.attrs?.id === rowId) {
        found = true;
        const currentStatus = node.attrs.codifyStatus;
        if (debugMode) console.log('[UPDATE STATUS] Found row:', rowId, 'current status:', currentStatus, 'new status:', status);

        if (currentStatus !== status) {
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, codifyStatus: status });
          updated = true;
          if (debugMode) console.log('[UPDATE STATUS] Updating row status');
        } else {
          if (debugMode) console.log('[UPDATE STATUS] Status already matches, no update needed');
        }
      }
      return true;
    });

    if (!found && debugMode) {
      console.error('[UPDATE STATUS] Row not found:', rowId);
    }

    if (updated) {
      if (debugMode) console.log('[UPDATE STATUS] Dispatching transaction');
      editor.view.dispatch(tr);
    }
  }, [editor, debugMode]);

  // Codify document using a specific specification
  // Initialize specification entries from document (no API calls)
  const initializeSpecificationEntries = useCallback((specification) => {
    if (!editor) return;

    console.log('[SPEC INIT] ========================================');
    console.log('[SPEC INIT] Initializing entries for specification:', specification.name);
    console.log('[SPEC INIT] Specification ID:', specification.specificationId || specification._id || specification.id);
    console.log('[SPEC INIT] Collection:', specification.codifyCollection);

    const specId = specification.specificationId || specification._id || specification.id;
    const codifiedData = new Map();

    // Get definitions for this specification to filter rows
    const specDefinitions = specificationDefinitionsRef.current.get(specId) || [];
    console.log('[SPEC INIT] Specification definitions count:', specDefinitions.length);
    console.log('[SPEC INIT] Specification definitions:', specDefinitions);

    // Create a set of valid heading types for this specification
    const validHeadingTypes = new Set(
      specDefinitions.map(def => (def.type || def.name)?.toLowerCase()).filter(Boolean)
    );
    console.log('[SPEC INIT] Valid heading types for this spec:', Array.from(validHeadingTypes));

    // Collect table rows from document that match this specification's definitions
    let totalRows = 0;
    let matchedRows = 0;
    let skippedRows = 0;

    editor.state.doc.descendants((node) => {
      if (node.type.name === "tableRow") {
        totalRows++;
        const rowId = node.attrs?.id;
        const headingType = node.attrs?.headingType;

        // Only include rows whose headingType is in this specification's definitions
        if (rowId && headingType && validHeadingTypes.has(headingType.toLowerCase())) {
          let inputText = "";
          node.forEach(cell => {
            if (cell.textContent) inputText += cell.textContent + " ";
          });
          inputText = inputText.trim();

          if (inputText) {
            // Initialize with 'new' state (no API call yet)
            codifiedData.set(rowId, {
              rowId,
              headingType,
              inputText,
              output: null,
              state: "new",
            });
            matchedRows++;
            console.log(`[SPEC INIT] ✅ MATCHED row ${headingType} - added to specification`);
          }
        } else if (rowId && headingType) {
          skippedRows++;
          console.log(`[SPEC INIT] ❌ SKIPPED row ${headingType} - not in specification definitions`);
        }
      }
    });

    console.log(`[SPEC INIT] Summary: Total=${totalRows}, Matched=${matchedRows}, Skipped=${skippedRows}`);

    if (debugMode) console.log(`[SPEC INIT] Found ${codifiedData.size} rows matching specification definitions`);

    // Store entries with 'new' state
    setSpecificationCodifications(prev => {
      console.log('[SPEC INIT] Previous specificationCodifications Map size:', prev.size);
      console.log('[SPEC INIT] Previous spec IDs:', Array.from(prev.keys()));
      const newMap = new Map(prev);
      console.log('[SPEC INIT] Setting codifications for spec ID:', specId);
      console.log('[SPEC INIT] Number of rows for this spec:', codifiedData.size);
      newMap.set(specId, new Map(codifiedData));
      console.log('[SPEC INIT] New specificationCodifications Map size:', newMap.size);
      console.log('[SPEC INIT] New spec IDs:', Array.from(newMap.keys()));
      console.log('[SPEC INIT] Rows per spec:', Array.from(newMap.entries()).map(([id, map]) => ({ specId: id, rowCount: map.size })));
      return newMap;
    });
  }, [editor, debugMode]);

  // Codify all rows for a specific specification
  const codifyAllForSpecification = useCallback(async (specification) => {
    if (!editor) return;

    if (debugMode) console.log('[SPEC CODIFY ALL] Starting codification for:', specification.name);

    const specId = specification.specificationId || specification._id || specification.id;
    const specData = specificationCodifications.get(specId);

    if (!specData || specData.size === 0) {
      if (debugMode) console.log('[SPEC CODIFY ALL] No entries to codify');
      return;
    }

    const rowsToCodeify = Array.from(specData.values());
    const codifiedData = new Map(specData);

    // Codify each row with the specification's settings
    // Process in batches to balance responsiveness and performance
    const batchSize = 5;
    for (let i = 0; i < rowsToCodeify.length; i++) {
      const { rowId, headingType, inputText } = rowsToCodeify[i];

      try {
        const payload = {
          collection: specification.codifyCollection,
          type: headingType,
          inputText,
          outputDataStandard: specification.dataStandard,
          outputDataType: specification.dataType,
          model: specification.model,
        };

        const res = await fetch(CODIFY_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const text = await res.text();
        let data;
        try { data = JSON.parse(text); } catch { data = {}; }

        let codifiedStr = "";
        if (data?.codifiedJSON) {
          if (typeof data.codifiedJSON === "string") {
            try { codifiedStr = JSON.stringify(JSON.parse(data.codifiedJSON), null, 2); }
            catch { codifiedStr = data.codifiedJSON; }
          } else {
            codifiedStr = JSON.stringify(data.codifiedJSON, null, 2);
          }
        }

        if (res.ok && codifiedStr) {
          codifiedData.set(rowId, {
            rowId,
            headingType,
            inputText,
            output: codifiedStr,
            state: "codified",
          });
        } else {
          codifiedData.set(rowId, {
            rowId,
            headingType,
            inputText,
            output: null,
            state: "error",
          });
        }
      } catch (err) {
        console.error(`[SPEC CODIFY ALL] Error codifying row ${rowId}:`, err);
        codifiedData.set(rowId, {
          rowId,
          headingType,
          inputText,
          output: null,
          state: "error",
        });
      }

      // Batch update: update state every batchSize rows or at the end
      if ((i + 1) % batchSize === 0 || i === rowsToCodeify.length - 1) {
        setSpecificationCodifications(prev => {
          const newMap = new Map(prev);
          newMap.set(specId, new Map(codifiedData));
          return newMap;
        });
      }
    }

    if (debugMode) console.log(`[SPEC CODIFY ALL] Completed codification for specification:`, specification.name);
  }, [editor, debugMode, specificationCodifications]);


  // Replace in-place codify call with queued task
  const enqueueCodify = (rowId, headingType, inputText) => {
    if (debugMode) console.log('[CODIFY] === ENQUEUEING CODIFY ===');
    if (debugMode) console.log('[CODIFY] rowId:', rowId);
    if (debugMode) console.log('[CODIFY] headingType:', headingType);
    if (debugMode) console.log('[CODIFY] inputText length:', inputText?.length);
    if (debugMode) console.log('[CODIFY] editableDocMeta.codifyCollection:', editableDocMeta.codifyCollection);
    if (debugMode) console.log('[CODIFY] coreDefinitions (from ref):', coreDefinitionsRef.current);
    if (debugMode) console.log('[CODIFY] codifySpecifications:', codifySpecifications);
    if (debugMode) console.log('[CODIFY] specificationDefinitions (from ref):', specificationDefinitionsRef.current);
    
    if (debugMode) {
      if (debugMode) console.log('[CODIFY] Current rowDataMap IDs:', Array.from(rowDataMapRef.current.keys()));
    }

    // Check which specification definitions contain this heading type
    const codificationsToRun = [];

    // Check each specification's definitions (use refs to get latest values)
    const currentSpecs = codifySpecificationsRef.current;
    const currentSpecDefs = specificationDefinitionsRef.current;
    const currentSpecCodifications = specificationCodificationsRef.current;

    if (debugMode) console.log('[CODIFY] Checking specifications, count:', currentSpecs.length);

    for (const spec of currentSpecs) {
      const specId = spec.specificationId || spec._id || spec.id;
      const specDefs = currentSpecDefs.get(specId) || [];
      if (debugMode) console.log(`[CODIFY] Checking spec "${spec.name}" (specId: ${specId}), definitions:`, specDefs);

      const headingInSpec = specDefs.some(def => {
        const match = def.type?.toLowerCase() === headingType?.toLowerCase() ||
                      def.name?.toLowerCase() === headingType?.toLowerCase();
        if (debugMode) console.log(`[CODIFY] Spec "${spec.name}" - Comparing "${headingType}" with def.type="${def.type}" def.name="${def.name}" - match:`, match);
        return match;
      });

      if (debugMode) console.log(`[CODIFY] headingInSpec for "${spec.name}":`, headingInSpec);

      if (headingInSpec) {
        // Always allow re-codification when explicitly triggered (e.g., by editing a cell)
        // The check for existing output was preventing re-codification after the first edit
        codificationsToRun.push({
          type: 'specification',
          specId: specId,
          specName: spec.name,
          collection: spec.codifyCollection,
          dataStandard: spec.dataStandard,
          dataType: spec.dataType,
          model: spec.model,
        });
        if (debugMode) console.log('[CODIFY] Adding to queue - heading found in specification:', spec.name, headingType);
      }
    }

    // If no specifications matched, cannot codify
    if (codificationsToRun.length === 0) {
      if (debugMode) console.log('[CODIFY] No specifications matched heading type:', headingType);
      return;
    }
    
    if (debugMode) console.log(`[CODIFY] Will run ${codificationsToRun.length} codification(s) for heading:`, headingType);

    // Mark running in UI
    setRowDataMap(prevMap => {
      const newMap = new Map(prevMap);
      const entry = newMap.get(rowId);
      if (!entry) {
        if (debugMode) console.error('[CODIFY] ERROR: No entry found for rowId:', rowId);
        return prevMap;
      }
      newMap.set(rowId, { ...entry, state: "running" });
      if (debugMode) console.log('[CODIFY] Updated entry state to running');
      return newMap;
    });

    // Mark all specification codifications as running
    codificationsToRun.forEach((codifConfig) => {
      if (codifConfig.type === 'specification') {
        setSpecificationCodifications(prevMap => {
          const newMap = new Map(prevMap);
          let specMap = newMap.get(codifConfig.specId);
          if (!specMap) {
            specMap = new Map();
            newMap.set(codifConfig.specId, specMap);
          }

          // Get existing entry to preserve any existing data
          const existingEntry = specMap.get(rowId);

          // Always mark as running since we're about to codify
          specMap.set(rowId, {
            ...existingEntry,
            rowId,
            state: "running",
            inputText,
            headingType,
          });

          return newMap;
        });
      }
    });
    updateRowStatus(rowId, "running");

    // Execute each codification (specifications only)
    codificationsToRun.forEach((codifConfig) => {
      const queueId = codifConfig.type === 'core'
        ? rowId
        : `${rowId}-spec-${codifConfig.specId}`;

      queueRef.current.enqueue(queueId, async (signal) => {
        const payload = {
          collection: codifConfig.collection,
          type: headingType || "Unknown",
          inputText,
          outputDataStandard: codifConfig.dataStandard,
          outputDataType: codifConfig.dataType,
          model: codifConfig.model,
        };

        if (debugMode) {
          if (debugMode) console.log(`[CODIFY] Executing ${codifConfig.type} codification:`, {
            rowId,
            queueId,
            collection: codifConfig.collection,
            specName: codifConfig.specName,
          });
        }

        const res = await fetch(CODIFY_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal,
          body: JSON.stringify(payload),
        });

        const text = await res.text();
        let data;
        try { data = JSON.parse(text); } catch { data = {}; }

        let codifiedStr = "";
        let hasDefinition = true;

        if (data?.codifiedJSON) {
          if (typeof data.codifiedJSON === "string") {
            try { codifiedStr = JSON.stringify(JSON.parse(data.codifiedJSON), null, 2); }
            catch { codifiedStr = data.codifiedJSON; }
          } else {
            codifiedStr = JSON.stringify(data.codifiedJSON, null, 2);
          }
        }

        // Check if the response indicates no matching definition
        // Common patterns: empty object {}, null, or specific error message
        if (codifiedStr === "{}" || codifiedStr === "null" ||
            data?.message?.toLowerCase().includes("no definition") ||
            data?.message?.toLowerCase().includes("not found")) {
          hasDefinition = false;
        }

        if (!res.ok) {
          const err = new Error(data?.message || "Codification failed");
          err.status = res.status;
          err.retriable = res.status === 429 || res.status >= 500;
          throw err;
        }

        // Success: update state based on codification type
        if (debugMode) {
          if (debugMode) console.log(`[CODIFY] ${codifConfig.type} success for rowId:`, rowId, 
            codifConfig.specName ? `(${codifConfig.specName})` : '', 
            'output length:', codifiedStr?.length);
        }

        if (codifConfig.type === 'core') {
          // Core codification - update rowDataMap
          setRowDataMap(prevMap => {
            const newMap = new Map(prevMap);
            const entry = newMap.get(rowId);
            if (entry) {
              newMap.set(rowId, {
                ...entry,
                state: hasDefinition ? "codified" : "no_definition",
                content: inputText,
                data: codifiedStr,
              });
            }
            if (debugMode) console.log('[CODIFY] Updated core entry in rowDataMap, hasDefinition:', hasDefinition);
            return newMap;
          });
          updateRowStatus(rowId, hasDefinition ? "done" : "no_definition");
        } else {
          // Specification codification - update specificationCodifications Map
          setSpecificationCodifications(prevMap => {
            const newMap = new Map(prevMap);
            let specMap = newMap.get(codifConfig.specId);
            if (!specMap) {
              specMap = new Map();
              newMap.set(codifConfig.specId, specMap);
            }
            specMap.set(rowId, {
              rowId,
              state: hasDefinition ? "codified" : "no_definition",
              inputText,
              headingType,
              output: codifiedStr,
            });
            if (debugMode) {
              console.log(`[CODIFY] Updated specification ${codifConfig.specName} entry for rowId:`, rowId, 'hasDefinition:', hasDefinition);
            }
            return newMap;
          });
          // Update visual status indicator in content cell
          updateRowStatus(rowId, "done");
        }

        return true;
      }).promise.catch(err => {
        if (err?.name === "AbortError") return;
        
        if (debugMode) {
          console.error(`[CODIFY] ${codifConfig.type} error for rowId:`, rowId, 
            codifConfig.specName ? `(${codifConfig.specName})` : '', 
            err?.message);
        }

        if (codifConfig.type === 'core') {
          // Mark core error
          setRowDataMap(prevMap => {
            const newMap = new Map(prevMap);
            const entry = newMap.get(rowId);
            if (entry) {
              newMap.set(rowId, { ...entry, state: "error" });
            }
            return newMap;
          });
          updateRowStatus(rowId, "error");
          toast({
            title: "Codify Error",
            description: err?.message || "Unknown error",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        } else {
          // Mark specification error
          setSpecificationCodifications(prevMap => {
            const newMap = new Map(prevMap);
            let specMap = newMap.get(codifConfig.specId);
            if (!specMap) {
              specMap = new Map();
              newMap.set(codifConfig.specId, specMap);
            }
            specMap.set(rowId, {
              state: "error",
              content: inputText,
              headingType,
            });
            return newMap;
          });
          // Update visual status indicator in content cell
          updateRowStatus(rowId, "error");
          toast({
            title: `Codify Error (${codifConfig.specName})`,
            description: err?.message || "Unknown error",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      });
    });
  };

  // Track content changes to mark rows as stale
  useEffect(() => {
    if (!editor) return;

    const lastCodifiedContent = new Map();
    rowDataMapRef.current.forEach((rowData, rowId) => {
      if (rowData.state === "codified" && rowData.content) {
        lastCodifiedContent.set(rowId, rowData.content);
      }
    });

    let contentChangeTimer = null;

    const handleContentChange = () => {
      if (contentChangeTimer) {
        clearTimeout(contentChangeTimer);
      }

      contentChangeTimer = setTimeout(() => {
        const { state } = editor;

        state.doc.descendants((node, pos) => {
          if (node.type.name !== "tableRow") return true;

          const rowId = node.attrs?.id;
          const codifyStatus = node.attrs?.codifyStatus;

          if (!rowId || codifyStatus !== "done") return true;

          let contentText = "";
          node.forEach((cell, offset, index) => {
            if (cell.attrs?.role === "content") {
              contentText = cell.textContent?.trim() || "";
            }
          });

          const lastContent = lastCodifiedContent.get(rowId);
          if (lastContent && contentText && contentText !== lastContent) {
            // Content changed - mark as 'changed'
            setRowDataMap(prevMap => {
              const newMap = new Map(prevMap);
              const entry = newMap.get(rowId);
              if (entry) {
                newMap.set(rowId, { ...entry, state: "changed" });
              }
              return newMap;
            });
            updateRowStatus(rowId, "changed");
            lastCodifiedContent.delete(rowId);
          }

          return true;
        });
      }, 300);
    };

    editor.on("update", handleContentChange);
    return () => {
      if (contentChangeTimer) clearTimeout(contentChangeTimer);
      editor.off("update", handleContentChange);
    };
  }, [editor, updateRowStatus]);

  // Refs for selection listener to avoid re-mounting
  const enqueueCodifyRef = useRef(enqueueCodify);
  const debugModeRef = useRef(debugMode);

  // Keep refs updated
  useEffect(() => {
    enqueueCodifyRef.current = enqueueCodify;
    debugModeRef.current = debugMode;
  }, [enqueueCodify, debugMode]);

  // Selection listener
  useEffect(() => {
    if (!editor) {
      console.log('[SELECTION SETUP] No editor, skipping');
      return;
    }

    console.log('[SELECTION SETUP] Setting up selection listener');

    const lastCellRef = { current: null };
    const lastProcessedTextRef = { current: "" };
    const cellEntryTextMap = { current: new Map() };
    let debounceTimer = null;

    const getTableCtx = ($pos) => {
      let rowDepth = null, cellDepth = null, cellPos = null;
      for (let d = $pos.depth; d > 0; d--) {
        const n = $pos.node(d);
        if ((n.type.name === "tableCell" || n.type.name === "tableHeader") && cellDepth === null) {
          cellDepth = d;
          cellPos = $pos.before(d);
        }
        if (n.type.name === "tableRow" && rowDepth === null) rowDepth = d;
      }
      if (rowDepth == null || cellDepth == null) return null;

      const rowNode = $pos.node(rowDepth);
      const cellNode = $pos.node(cellDepth);
      const rowIndex = $pos.index(rowDepth) + 1;
      const colIndex = $pos.index(cellDepth) + 1;

      const cellAttrs = cellNode.attrs || {};
      const rowAttrs = rowNode.attrs || {};

      const headingType = cellAttrs.headingType ?? rowAttrs.headingType ?? "Unknown";
      let role = cellAttrs.role;
      if (!role) {
        if (colIndex === 1) role = "heading";
        else if (colIndex === 2) role = "content";
        else role = "output";
      }
      const rowId = rowAttrs?.id;

      return {
        rowIndex, colIndex, headingType, role,
        text: cellNode?.textContent?.trim() ?? "",
        rowId,
        cellPos,
      };
    };

    const handleSelectionUpdate = () => {
      console.log('[SELECTION EVENT] selectionUpdate event fired!', {
        hasFocus: editor?.view?.hasFocus?.(),
        hasEditor: !!editor
      });

      if (!editor?.view?.hasFocus?.()) {
        console.log('[SELECTION EVENT] Editor does not have focus, skipping');
        return;
      }

      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      debounceTimer = setTimeout(() => {
        processSelectionUpdate();
      }, 100);
    };

    const processSelectionUpdate = () => {
      const debug = debugModeRef.current;

      if (!editor?.view?.hasFocus?.()) {
        if (debug) {
          console.log('[SELECTION] Editor does not have focus, skipping');
        }
        return;
      }

      const { selection } = editor.state;

      if (!selection.empty) {
        if (debug) console.log('[SELECTION] Active text selection detected, skipping codify trigger');
        return;
      }

      const ctxNow = getTableCtx(selection.$from);
      const prevCtx = lastCellRef.current;

      if (debug) {
        console.log('[SELECTION] Selection update:', {
          prevCell: prevCtx ? `${prevCtx.headingType} (${prevCtx.role})` : 'none',
          currentCell: ctxNow ? `${ctxNow.headingType} (${ctxNow.role})` : 'none',
          sameCell: prevCtx && ctxNow && prevCtx.cellPos === ctxNow.cellPos
        });
      }

      if (prevCtx && ctxNow && prevCtx.cellPos === ctxNow.cellPos) {
        return;
      }

      if (debug && ctxNow && (!prevCtx || prevCtx.cellPos !== ctxNow.cellPos)) {
        setCellInfo(prev => ({
          current: {
            headingType: ctxNow.headingType,
            rowIndex: ctxNow.rowIndex,
            colIndex: ctxNow.colIndex,
            rowId: ctxNow.rowId
          },
          previous: prev.current || prev.previous
        }));
      }

      const enteredContentCell = ctxNow?.role === "content" &&
        (!prevCtx || ctxNow.cellPos !== prevCtx.cellPos);

      if (enteredContentCell) {
        const entryText = ctxNow.text ?? "";
        if (debug) console.log('[SELECTION] Entered content cell, saving entry text:', entryText.substring(0, 50));
        cellEntryTextMap.current.set(ctxNow.cellPos, entryText);
      }

      lastCellRef.current = ctxNow;

      const leftContentCell = prevCtx?.role === "content" &&
        (!ctxNow || ctxNow.cellPos !== prevCtx.cellPos);

      console.log('[SELECTION] Left content cell check:', {
        prevRole: prevCtx?.role,
        prevCellPos: prevCtx?.cellPos,
        ctxNowRole: ctxNow?.role,
        ctxNowCellPos: ctxNow?.cellPos,
        leftContentCell,
        hasCtxNow: !!ctxNow
      });

      if (!leftContentCell) return;

      let currentText = "";
      const { state } = editor;
      state.doc.descendants((node, pos) => {
        if (pos === prevCtx.cellPos && (node.type.name === "tableCell" || node.type.name === "tableHeader")) {
          currentText = node.textContent?.trim() ?? "";
          return false;
        }
        return true;
      });

      const cellEntryText = cellEntryTextMap.current.get(prevCtx.cellPos) ?? "";

      if (debug) {
        console.log('[CODIFY CHECK] Left content cell:', {
          rowId: prevCtx.rowId?.substring(0, 8),
          headingType: prevCtx.headingType,
          currentTextLength: currentText.length,
          cellEntryTextLength: cellEntryText.length,
          textChanged: currentText !== cellEntryText,
          currentTextPreview: currentText.substring(0, 50),
          cellEntryTextPreview: cellEntryText.substring(0, 50)
        });
      }

      console.log('[CODIFY CHECK] Text comparison:', {
        currentText: currentText.substring(0, 50),
        currentTextLength: currentText.length,
        cellEntryText: cellEntryText.substring(0, 50),
        cellEntryTextLength: cellEntryText.length,
        textChanged: currentText !== cellEntryText
      });

      if (!currentText || currentText.length <= 3 || currentText === cellEntryText) {
        console.log('[CODIFY CHECK] Skipping codify:', {
          reason: !currentText ? 'no text' : currentText.length <= 3 ? 'text too short' : 'text unchanged'
        });
        return;
      }

      lastProcessedTextRef.current = currentText;
      // DO NOT update cellEntryTextMap here - it should only be set when entering the cell
      // so that future edits to the same cell can be detected
      // cellEntryTextMap.current.set(prevCtx.cellPos, currentText);

      const rowId = prevCtx.rowId;
      const headingType = prevCtx.headingType || "Unknown";

      console.log('[CODIFY CHECK] About to call enqueueCodify:', { rowId, headingType, textLength: currentText.length });

      if (!rowId) {
        console.log('[CODIFY CHECK] No rowId, skipping');
        return;
      }

      console.log('[CODIFY CHECK] ✅ Calling enqueueCodify NOW');
      enqueueCodifyRef.current(rowId, headingType, currentText);
    };

    console.log('[SELECTION SETUP] Attaching selectionUpdate event listener to editor');
    editor.on("selectionUpdate", handleSelectionUpdate);

    // ALSO listen to transaction event as fallback (more reliable with Collaboration)
    const handleTransaction = ({ transaction }) => {
      // Only process if selection actually changed
      if (!transaction.selectionSet) return;
      console.log('[SELECTION EVENT] transaction event with selection change detected!');
      handleSelectionUpdate();
    };

    console.log('[SELECTION SETUP] Also attaching transaction event listener as fallback');
    editor.on("transaction", handleTransaction);

    // Test if the event is actually registered
    setTimeout(() => {
      console.log('[SELECTION SETUP] Event listeners attached. Waiting for selection changes...');
    }, 1000);

    return () => {
      console.log('[SELECTION SETUP] Removing event listeners');
      if (debounceTimer) clearTimeout(debounceTimer);
      editor.off("selectionUpdate", handleSelectionUpdate);
      editor.off("transaction", handleTransaction);
    };
  }, [editor]); // Only depend on editor to prevent constant re-mounting

  // Function to re-run codify on current row (used by both Ctrl+R and button click)
  const handleCodifyCurrentRow = useCallback(() => {
    if (!editor) return;

    const { state } = editor;
    const { selection } = state;

    // Find the current table row
    let rowNode = null;
    let rowId = null;
    let headingType = null;

    state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
      if (node.type.name === 'tableRow' && !rowNode) {
        rowNode = node;
        rowId = node.attrs?.id;
        headingType = node.attrs?.headingType;
      }
    });

    if (!rowId || !headingType) {
      if (debugMode) console.log('[CODIFY NOW] Not in a table row');
      toast({
        title: "No row selected",
        description: "Place cursor in a table row to re-run codify",
        status: "info",
        duration: 2000,
      });
      return;
    }

    // Get the content text from the row
    let inputText = "";
    rowNode.forEach(cell => {
      if (cell.textContent) inputText += cell.textContent + " ";
    });
    inputText = inputText.trim();

    if (!inputText || inputText.length <= 3) {
      if (debugMode) console.log('[CODIFY NOW] Row content too short');
      toast({
        title: "No content",
        description: "Row must have content to codify",
        status: "info",
        duration: 2000,
      });
      return;
    }

    if (debugMode) console.log('[CODIFY NOW] Re-running codify for row:', rowId, headingType);

    // Clear existing outputs for this row to force re-codification
    setSpecificationCodifications(prev => {
      const newMap = new Map(prev);
      newMap.forEach((specMap, specId) => {
        const entry = specMap.get(rowId);
        if (entry) {
          specMap.set(rowId, {
            ...entry,
            output: null,
            state: "new",
          });
        }
      });
      return newMap;
    });

    // Trigger codify
    setTimeout(() => {
      enqueueCodify(rowId, headingType, inputText);
    }, 100);

    toast({
      title: "Re-running codify",
      description: `Re-codifying ${headingType}`,
      status: "info",
      duration: 2000,
    });
  }, [editor, debugMode, toast, enqueueCodify, setSpecificationCodifications]);

  // Keyboard shortcut: Ctrl+R / Cmd+R to re-run codify on current row
  useEffect(() => {
    if (!editor) return;

    const handleKeyDown = (event) => {
      // Check for Ctrl+R (Windows/Linux) or Cmd+R (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 'r') {
        event.preventDefault();
        handleCodifyCurrentRow();
      }
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener('keydown', handleKeyDown);

    return () => {
      editorElement.removeEventListener('keydown', handleKeyDown);
    };
  }, [editor, handleCodifyCurrentRow]);

  // Save function that saves to codifications API
  const handleSave = async (isAutoSave = false) => {
    if (!editor) {
      if (debugMode) console.error('[SAVE ERROR] No editor instance available');
      return;
    }
    const repoId = documentMeta?.documentRepositoryId;
    if (!repoId) {
      if (debugMode) console.error('[SAVE ERROR] No documentRepositoryId in documentMeta:', documentMeta);
      if (!isAutoSave) {
        toast({ title: "No linked document", description: "Link a document in the repository before saving.", status: "warning", duration: 3500, isClosable: true });
      }
      return;
    }

    if (isAutoSave) {
      setIsAutoSaving(true);
    }

    const userId = user?._id ||
                   user?.platformUser?._id ||
                   user?.platformUser?.id ||
                   user?.linkedInId ||
                   user?.sub ||
                   user?.email ||
                   null;

    if (debugMode) {
      if (debugMode) console.log('[SAVE] User ID resolution:', {
        userId,
        hasPlatformUser: !!user?.platformUser,
        platformUserKeys: user?.platformUser ? Object.keys(user.platformUser) : [],
      });
    }

    try {
      // ⭐ Save document WITHOUT codifiedContents (just content)
      const documentPayload = {
        definitionId: documentMeta?.definitionId ?? "",
        name: editableDocMeta.name || "Untitled Document",
        content: JSON.stringify(editor.getJSON()),
        // ⭐ NO codifiedContents field anymore
        createdDate: documentMeta?.createdDate ?? new Date().toISOString(),
        updatedBy: userId,
        updatedDate: new Date().toISOString(),
        codifyCollection: editableDocMeta.codifyCollection,
        dataStandard: editableDocMeta.dataStandard,
        dataType: editableDocMeta.dataType,
        model: editableDocMeta.model,
        productCode: documentMeta?.productCode || contract?.productCode,
        isTemplate: documentMeta?.isTemplate,
        codifySpecifications: codifySpecifications,
      };

      if (debugMode) {
        if (debugMode) console.log('[SAVE] Saving document:', {
          repoId,
          url: `${DOCS_API}/${repoId}`,
          payloadSize: JSON.stringify(documentPayload).length,
        });
      }

      const docRes = await fetch(`${DOCS_API}/${repoId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(documentPayload, null, 2),
      });

      if (!docRes.ok) {
        const errorText = await docRes.text().catch(() => "");
        if (debugMode) {
          console.error('[SAVE ERROR] Document save failed:', {
            status: docRes.status,
            statusText: docRes.statusText,
            errorText
          });
        }
        throw new Error(errorText || "Failed to save document to repository");
      }

      if (debugMode) console.log('[SAVE SUCCESS] Document saved');

      // Save ONLY specification codifications (no MainCodification anymore)
      let updatedCodifySpecs = [...codifySpecifications];

      if (codifySpecifications.length > 0) {
        console.log('[SAVE] ========================================');
        console.log('[SAVE] Saving specification codifications, count:', codifySpecifications.length);
        console.log('[SAVE] Current specificationCodifications Map:', {
          size: specificationCodifications.size,
          keys: Array.from(specificationCodifications.keys()),
          details: Array.from(specificationCodifications.entries()).map(([id, map]) => ({
            specId: id,
            rowCount: map.size,
            states: Array.from(map.values()).map(r => r.state)
          }))
        });

        for (const spec of codifySpecifications) {
          const specId = spec.specificationId || spec._id || spec.id;
          console.log('[SAVE] Checking spec:', spec.name, 'ID:', specId);
          const specCodifMap = specificationCodifications.get(specId);

          if (!specCodifMap || specCodifMap.size === 0) {
            console.log('[SAVE] ❌ No codifications for spec:', spec.name, 'specId:', specId, '- SKIPPING');
            console.log('[SAVE] specCodifMap exists?', !!specCodifMap, 'size:', specCodifMap?.size);
            continue;
          }

          console.log('[SAVE] ✅ Processing spec:', spec.name, 'with', specCodifMap.size, 'rows');

          // Build rows array from specification codifications
          const specRows = [];
          let rowIdx = 0;
          
          // Get segment info and heading text from the actual table rows
          const rowMetadataMap = new Map();
          editor.state.doc.descendants((node) => {
              if (node.type.name === "tableRow" && node.attrs?.id) {
                const segmentName = node.attrs?.segmentName || node.attrs?.segmentType || "Unknown";
                let headingText = "";
                let colIndex = 0;
                node.forEach((cellNode) => {
              colIndex += 1;
              if (cellNode.attrs?.role === "heading" || colIndex === 1) {
                headingText = cellNode.textContent?.trim() || "";
              }
            });
            rowMetadataMap.set(node.attrs.id, { segmentName, headingText });
          }
          });
          
          for (const [rowId, rowData] of specCodifMap.entries()) {
            const metadata = rowMetadataMap.get(rowId) || { segmentName: "Unknown", headingText: "" };
            specRows.push({
              id: rowId,
              row: rowIdx,
              col: 1,
              segment: metadata.segmentName,
              type: rowData.headingType || "Unknown",
              heading: metadata.headingText,
              content: rowData.inputText || rowData.content || "",
              data: rowData.output || rowData.data || "",
              state: rowData.state || "new",
            });
            rowIdx++;
          }

          const specCodificationPayload = {
            documentId: repoId,
            participantId: userId,
            participantRole: selectedParticipantRole,
            specificationId: specId,
            specificationName: spec.name,
            collection: spec.codifyCollection,
            type: "SpecificationCodification",
            outputDataStandard: spec.dataStandard,
            outputDataType: spec.dataType,
            model: spec.model,
            output: null,
            state: "codified",
            rows: specRows,
            createdBy: userId,
            updatedBy: userId,
          };

          console.log('[SAVE] Preparing to save specification codification:', {
            specName: spec.name,
            specId: specId,
            rowsCount: specRows.length,
            sampleRows: specRows.slice(0, 2),
            payload: specCodificationPayload
          });

          // Check if we have an existing codification ID for this specification
          // First check the spec itself, then fall back to in-memory map
          const existingCodifId = spec.codificationId || specificationCodificationIds.get(specId);
          const specCodifUrl = existingCodifId
            ? `${CODIFICATIONS_API}/${existingCodifId}`
            : CODIFICATIONS_API;
          const specCodifMethod = existingCodifId ? 'PUT' : 'POST';

          console.log('[SAVE] Using', specCodifMethod, 'for spec codification:', {
            specName: spec.name,
            existingCodifId,
            url: specCodifUrl,
          });

          const specCodifRes = await fetch(specCodifUrl, {
            method: specCodifMethod,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(specCodificationPayload, null, 2),
          });

          if (!specCodifRes.ok) {
            const errorText = await specCodifRes.text().catch(() => "");
            console.error('[SAVE ERROR] Specification codification save failed:', {
              specName: spec.name,
              status: specCodifRes.status,
              statusText: specCodifRes.statusText,
              errorText
            });
            // Don't throw - continue saving other specs
          } else {
            const savedSpecCodif = await specCodifRes.json().catch(() => null);
            console.log('[SAVE RESPONSE] Received response for spec:', spec.name, savedSpecCodif);

            if (savedSpecCodif) {
              // Update the local copy of codifySpecifications with the codificationId
              updatedCodifySpecs = updatedCodifySpecs.map(s => {
                const sId = s.specificationId || s._id || s.id;
                return sId === specId
                  ? { ...s, codificationId: savedSpecCodif._id }
                  : s;
              });

              // Store the codification ID for future updates
              setSpecificationCodificationIds(prev => {
                const newMap = new Map(prev);
                newMap.set(specId, savedSpecCodif._id);
                return newMap;
              });

              console.log('[SAVE SUCCESS] ✅ Specification codification saved:', {
                specName: spec.name,
                codificationId: savedSpecCodif._id,
                rowCount: savedSpecCodif.rows?.length
              });
            }
          }
        }
        
        // Update state with all codificationIds
        setCodifySpecifications(updatedCodifySpecs);
        
        // Save document again with updated codifySpecifications including codificationIds
        if (debugMode) {
          if (debugMode) console.log('[SAVE] Updating document with codificationIds:', updatedCodifySpecs);
        }
        
        const updatedDocPayload = {
          ...documentPayload,
          codifySpecifications: updatedCodifySpecs,
        };
        
        const docUpdateRes = await fetch(`${DOCS_API}/${repoId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedDocPayload, null, 2),
        });
        
        if (!docUpdateRes.ok) {
          console.warn('[SAVE WARNING] Failed to update document with codificationIds');
        } else {
          if (debugMode) console.log('[SAVE SUCCESS] Document updated with codificationIds');
        }
      }

      if (!isAutoSave) {
        toast({ title: "Document Saved", status: "success", duration: 2500, isClosable: true });
      }

      setHasUnsavedChanges(false);
      setLastSavedAt(new Date());
      setIsAutoSaving(false);

      if (onSave) {
        if (debugMode) console.log('[SAVE] Calling onSave callback');
        onSave(documentPayload);
      }
    } catch (err) {
      if (debugMode) console.error('[SAVE ERROR] Exception during save:', err);
      setIsAutoSaving(false);
      if (!isAutoSave) {
        toast({ title: "Save Failed", description: err?.message || "Unknown error", status: "error", duration: 4000, isClosable: true });
      }
    }
  };

  // Auto-save with debouncing - saves 5 seconds after last change
  useEffect(() => {
    if (!editor || !hasUnsavedChanges) return;

    const autoSaveTimer = setTimeout(() => {
      if (hasUnsavedChanges) {
        if (debugMode) console.log('[AUTO-SAVE] Triggering auto-save');
        handleSave(true); // isAutoSave = true
      }
    }, 5000); // 5 seconds debounce

    return () => clearTimeout(autoSaveTimer);
  }, [hasUnsavedChanges, editor, debugMode, handleSave]);

  // Track changes to mark document as unsaved
  useEffect(() => {
    if (!editor) return;

    const handleUpdate = () => {
      setHasUnsavedChanges(true);
    };

    editor.on('update', handleUpdate);
    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor]);

  const handleClose = () => {
    if (debugMode) console.log("[DocumentEditorView] handleClose called, onClose:", onClose);
    if (onClose) {
      if (debugMode) console.log("[DocumentEditorView] Calling onClose callback");
      onClose();
    } else {
      if (debugMode) console.log("[DocumentEditorView] No onClose callback, using window.history.back()");
      window.history.back();
    }
  };

  const handleOpenMetadataEditor = useCallback((rowId) => {
    if (!editor) return;

    const { state } = editor;
    let rowMetadata = null;

    state.doc.descendants((node, pos) => {
      if (node.type.name === "tableRow" && node.attrs?.id === rowId) {
        rowMetadata = {
          id: rowId,
          segmentType: node.attrs?.segmentType || "",
          segmentName: node.attrs?.segmentName || "",
          fieldId: node.attrs?.fieldId || "",
          headingType: node.attrs?.headingType || "",
          editableBy: node.attrs?.editableBy || [],
          adminBy: node.attrs?.adminBy || [],
        };
        return false;
      }
      return true;
    });

    if (rowMetadata) {
      setSelectedRowMetadata(rowMetadata);
      setIsMetadataEditorOpen(true);
    }
  }, [editor]);

  const handleSaveMetadata = useCallback((updatedMetadata) => {
    if (!editor || !selectedRowMetadata) return;

    const { state } = editor;
    let tr = state.tr;
    let updated = false;

    state.doc.descendants((node, pos) => {
      if (node.type.name === "tableRow" && node.attrs?.id === selectedRowMetadata.id) {
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          segmentType: updatedMetadata.segmentType,
          segmentName: updatedMetadata.segmentName,
          fieldId: updatedMetadata.fieldId,
          headingType: updatedMetadata.headingType,
          editableBy: updatedMetadata.editableBy,
          adminBy: updatedMetadata.adminBy,
        });
        updated = true;
        return false;
      }
      return true;
    });

    if (updated) {
      editor.view.dispatch(tr);
    }
  }, [editor, selectedRowMetadata]);

  const handleStatusDotClick = useCallback((rowId) => {
    if (debugMode) console.log("[DOT CLICK] rowId:", rowId);

    setDrawerOpen(prev => {
      const wasOpen = prev;
      if (debugMode) console.log("[DOT CLICK] drawer wasOpen:", wasOpen);

      // Find index in document order
      let index = -1;
      let counter = 0;
      editor.state.doc.descendants((node) => {
        if (node.type.name === "tableRow") {
          if (node.attrs?.id === rowId) {
            index = counter;
            return false;
          }
          counter++;
        }
        return true;
      });

      if (debugMode) console.log("[DOT CLICK] found index:", index);
      if (index === -1) return prev;

      setExpandedAccordionIndices(prevIndices => {
        if (prevIndices.includes(index)) {
          if (debugMode) console.log("[DOT CLICK] index already expanded");
          return prevIndices;
        }
        const newIndices = [...prevIndices, index];
        if (debugMode) console.log("[DOT CLICK] expanding accordion index:", index, "new indices:", newIndices);
        return newIndices;
      });

      setTimeout(() => {
        const accordionItem = document.querySelector(`[data-codify-id="${rowId}"]`);
        if (debugMode) console.log("[DOT CLICK] accordion item found:", !!accordionItem);
        if (accordionItem) {
          accordionItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, wasOpen ? 0 : 300);

      return true;
    });
  }, [editor, debugMode]);

  useEffect(() => {
    if (!editor) return;

    const handleClick = (event) => {
      const firstCell = event.target.closest('td:first-child, th:first-child');
      if (firstCell) {
        const rect = firstCell.getBoundingClientRect();
        const clickX = event.clientX;
        const clickY = event.clientY;

        const iconLeft = rect.left - 28;
        const iconRight = rect.left;
        const iconTop = rect.top;
        const iconBottom = rect.top + 32;

        const isInIconArea = clickX >= iconLeft && clickX <= iconRight &&
                            clickY >= iconTop && clickY <= iconBottom;

        if (debugMode) {
          if (debugMode) console.log('[GEAR CLICK] Click detected:', {
            clickX,
            clickY,
            iconLeft,
            iconRight,
            iconTop,
            iconBottom,
            isInIconArea,
            cellTop: rect.top,
            cellHeight: rect.height
          });
        }

        if (isInIconArea) {
          const row = event.target.closest('tr[data-id]');
          if (row) {
            const rowId = row.getAttribute('data-id');
            if (rowId) {
              event.preventDefault();
              event.stopPropagation();
              handleOpenMetadataEditor(rowId);
              return;
            }
          }
        }
      }

      const cell = event.target.closest('td:nth-child(2), th:nth-child(2), td[data-role="content"], th[data-role="content"]');
      if (!cell) return;

      const rect = cell.getBoundingClientRect();
      const clickX = event.clientX;
      const clickY = event.clientY;

      const dotLeft = rect.right - 24;
      const dotTop = rect.top + (rect.height / 2) - 12;
      const dotBottom = rect.top + (rect.height / 2) + 12;

      if (clickX >= dotLeft && clickX <= rect.right &&
          clickY >= dotTop && clickY <= dotBottom) {
        const row = event.target.closest('tr[data-id]');
        if (!row) {
          if (debugMode) {
            if (debugMode) console.log("[DOT CLICK] Click detected in dot area but no row found");
          }
          return;
        }

        const rowId = row.getAttribute('data-id');
        if (rowId) {
          if (debugMode) console.log("[DOT CLICK] Click detected on dot for rowId:", rowId);
          event.preventDefault();
          event.stopPropagation();
          handleStatusDotClick(rowId);
        }
      }
    };

    const editorEl = editor.view.dom;
    editorEl.addEventListener('click', handleClick, true);

    return () => {
      editorEl.removeEventListener('click', handleClick, true);
    };
  }, [editor, handleStatusDotClick, handleOpenMetadataEditor]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const containerWidth = window.innerWidth;
      const newWidth = ((containerWidth - e.clientX) / containerWidth) * 100;
      setPanelWidth(Math.min(Math.max(newWidth, 15), 50));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Build display array for accordion - merge rowDataMap AND specificationCodifications
  const displayRows = React.useMemo(() => {
    const rows = [];
    editor?.state.doc.descendants((node) => {
      if (node.type.name === "tableRow") {
        const rowId = node.attrs?.id;
        if (!rowId) return true;

        // Check rowDataMap first (core codifications)
        const rowData = rowDataMap.get(rowId);
        if (rowData) {
          rows.push({
            id: rowId,
            type: rowData.heading || node.attrs?.headingType || "Unknown",
            state: rowData.state || "new",
            output: rowData.data,
          });
          return true; // Continue to next row
        }

        // If not in rowDataMap, check specification codifications
        let foundInSpec = false;
        specificationCodifications.forEach((specRowMap, specId) => {
          const specRowData = specRowMap.get(rowId);
          if (specRowData && !foundInSpec) {
            rows.push({
              id: rowId,
              type: specRowData.headingType || node.attrs?.headingType || "Unknown",
              state: specRowData.state || "codified",
              output: specRowData.output,
            });
            foundInSpec = true;
          }
        });

        // If not in either, create a placeholder entry
        if (!foundInSpec) {
          rows.push({
            id: rowId,
            type: node.attrs?.headingType || "Unknown",
            state: "new",
            output: null,
          });
        }
      }
      return true;
    });
    return rows;
  }, [editor, rowDataMap, specificationCodifications]);

  return (
    <Box height="100vh" display="flex" flexDirection="column" overflow="hidden">
      <Navbar />

      <DocumentMetadataEditor
        isOpen={isMetadataEditorOpen}
        onClose={() => setIsMetadataEditorOpen(false)}
        rowData={selectedRowMetadata}
        userRoles={effectiveUserRoles}
        onSave={handleSaveMetadata}
      />

      <Box position="fixed" width="100%" height="100%" top={0} left={0} zIndex={0}>
        <Image src={HeroImage} alt="Hero Watermark" width="100%" height="100%" objectFit="cover" opacity={0.35} filter="blur(1px)" />
      </Box>

      <Box position="relative" zIndex={1} p={10} flex="1" display="flex" flexDirection="column" overflow="hidden">

        <Box borderWidth="1px" borderRadius="md" p={3} mb={2} borderColor="gray.500" backgroundColor="whiteAlpha.900" flexShrink={0}>
          <HStack spacing={6} align="flex-start" w="full">
            <Tooltip label="Back to the Product Dashboard">
              <IconButton icon={<HiOutlineArrowNarrowLeft />} size="md" onClick={handleClose} />
            </Tooltip>
            <Input
              value={editableDocMeta.name}
              onChange={(e) =>
                setEditableDocMeta((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              size="lg"
              fontSize="2xl"
              fontWeight="bold"
              variant="unstyled"
              placeholder="Document Name"
              _placeholder={{ color: "gray.400" }}
              flex="1"
              maxW="1000px"
            />
            {documentMeta?.isTemplate && (
              <Box
                px={3}
                py={1}
                borderRadius="md"
                bg="blue.100"
                borderWidth="1px"
                borderColor="blue.300"
                fontSize="sm"
                fontWeight="semibold"
                color="blue.700"
                whiteSpace="nowrap"
              >
                Template
              </Box>
            )}
            <Box ml="auto" display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
              <HStack spacing={2}>
                <Button
                  leftIcon={<HiOutlinePrinter />}
                  colorScheme="gray"
                  onClick={async () => {
                    const repoId = documentMeta?.documentRepositoryId;
                    if (!repoId) {
                      toast({
                        title: 'Error',
                        description: 'No document ID found',
                        status: 'error',
                        duration: 3000,
                      });
                      return;
                    }

                    toast({
                      title: 'Generating PDF...',
                      description: 'Please wait while we create your PDF',
                      status: 'info',
                      duration: 2000,
                    });

                    try {
                      const response = await fetch(`${DOCS_API}/${repoId}/render/pdf`, {
                        method: 'POST',
                      });

                      if (!response.ok) {
                        throw new Error(`Failed to generate PDF: ${response.statusText}`);
                      }

                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${editableDocMeta.name || 'document'}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);

                      toast({
                        title: 'PDF Generated!',
                        description: `Downloaded as ${a.download}`,
                        status: 'success',
                        duration: 3000,
                      });
                    } catch (error) {
                      console.error('PDF generation error:', error);
                      toast({
                        title: 'Error generating PDF',
                        description: error.message,
                        status: 'error',
                        duration: 5000,
                      });
                    }
                  }}
                  size="md"
                >
                  Export PDF
                </Button>
                <Button
                  leftIcon={<HiOutlineSave />}
                  colorScheme="blue"
                  onClick={handleSave}
                  size="md"
                >
                  Save
                </Button>
              </HStack>
              <VStack spacing={0} align="flex-end">
                {isAutoSaving && (
                  <Text fontSize="xs" color="blue.500" fontWeight="medium">
                    Auto-saving...
                  </Text>
                )}
                {!isAutoSaving && lastSavedAt && (
                  <Text fontSize="xs" color="gray.500">
                    Last saved: {lastSavedAt.toLocaleTimeString()}
                  </Text>
                )}
                {hasUnsavedChanges && !isAutoSaving && (
                  <Text fontSize="xs" color="orange.500">
                    Unsaved changes
                  </Text>
                )}
                <HStack spacing={1} align="center">
                  <Text fontSize="xs" color="gray.600">
                    Queue: {queueRef.current.size}
                  </Text>
                  <QueueMonitor queueRef={queueRef} />
                </HStack>
              </VStack>
            </Box>
          </HStack>
        </Box>

        {debugMode && (
          <Box bg="yellow.100" border="1px solid" borderColor="yellow.400" p={2} mb={2} borderRadius="md" flexShrink={0}>
            <Text as="strong" fontSize="xs">Current Cell:</Text>{" "}
            {cellInfo.current ? `${cellInfo.current.headingType || "N/A"} | Row: ${cellInfo.current.rowIndex ?? "?"} | Col: ${cellInfo.current.colIndex ?? "?"} | RowID: ${cellInfo.current.rowId ? cellInfo.current.rowId.substring(0, 8) + '...' : 'N/A'}` : "None"}
            <br />
            <Text as="strong" fontSize="xs">Previous Cell:</Text>{" "}
            {cellInfo.previous ? `${cellInfo.previous.headingType || "N/A"} | Row: ${cellInfo.previous.rowIndex ?? "?"} | Col: ${cellInfo.previous.colIndex ?? "?"} | RowID: ${cellInfo.previous.rowId ? cellInfo.previous.rowId.substring(0, 8) + '...' : 'N/A'}` : "None"}
          </Box>
        )}

        <Box
          className="mrc-editor"
          display="flex"
          flexDirection="column"
          borderWidth="1px"
          borderRadius="md"
          borderColor="gray.500"
          backgroundColor="whiteAlpha.900"
          flex="1"
          overflow="hidden"
        >
          <PlacingProcessBar
            currentStage={placingStage}
            currentStepCode={placingStepCode}
            participantRole={participantRole}
            onStepChange={(stage, stepCode) => {
              setPlacingStage(stage);
              setPlacingStepCode(stepCode);
            }}
            onStageChange={(stage, stepCode) => {
              setPlacingStage(stage);
              setPlacingStepCode(stepCode);
              toast({
                title: `Switched to ${getStageDisplayName(stage)} Stage`,
                status: "info",
                duration: 2000,
                isClosable: true
              });
            }}
          />

          <Box borderBottom="1px" borderColor="gray.300" p={2} backgroundColor="whiteAlpha.900" flexShrink={0}>
            <MenuBar
              editor={editor}
              onSave={handleSave}
              onClose={handleClose}
              onShowCodified={() => setDrawerOpen(!isDrawerOpen)}
              onCodifyCurrentRow={handleCodifyCurrentRow}
              debugMode={debugMode}
              onToggleDebug={() => setDebugMode(!debugMode)}
              isSuperAdmin={isSuperAdmin}
              codifyCollection={editableDocMeta.codifyCollection}
              documentDefinitionId={documentMeta?.definitionId}
            />
          </Box>

          <HStack flex="1" spacing={0} alignItems="stretch" overflow="hidden">
            <Box
              flex="1"
              overflowY="auto"
              overflowX="hidden"
              w={isDrawerOpen ? `${100 - panelWidth}%` : "100%"}
            >
              <div className="editor-wrapper" style={{ zoom }}>
                <div
                  className="editor-page"
                  onContextMenu={(e) => {
                    e.preventDefault();
                    if (editor && !editor.state.selection.empty) {
                      // If text is already selected, BubbleMenu will show automatically
                      return;
                    }
                    // If no selection, select the word at cursor position
                    if (editor) {
                      const { from } = editor.state.selection;
                      const $pos = editor.state.doc.resolve(from);
                      const word = editor.state.doc.textBetween(
                        Math.max(0, $pos.before()),
                        Math.min(editor.state.doc.content.size, $pos.after())
                      );
                      if (word) {
                        editor.commands.setTextSelection({
                          from: $pos.before(),
                          to: $pos.after()
                        });
                      }
                    }
                  }}
                >
                  <EditorContent editor={editor} />
                  {editor && (
                    <BubbleMenu
                      editor={editor}
                      tippyOptions={{
                        duration: 100,
                        placement: 'top-start',
                        offset: [0, -10], // [horizontal, vertical] - negative to move closer/overlap
                        appendTo: () => document.body,
                      }}
                      shouldShow={({ editor, view, state, oldState, from, to }) => {
                        // Don't show if link input is active
                        if (showLinkInput) return false;
                        // Only show if there's an actual selection
                        return from !== to;
                      }}
                    >
                      <HStack
                        bg="white"
                        color="gray.800"
                        px={2}
                        py={1}
                        borderRadius="md"
                        boxShadow="lg"
                        border="1px solid"
                        borderColor="gray.200"
                        spacing={1}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <IconButton
                          size="xs"
                          icon={<strong>B</strong>}
                          onClick={() => editor.chain().focus().toggleBold().run()}
                          bg={editor.isActive('bold') ? 'blue.100' : 'transparent'}
                          color={editor.isActive('bold') ? 'blue.600' : 'gray.700'}
                          _hover={{ bg: editor.isActive('bold') ? 'blue.200' : 'gray.100' }}
                          aria-label="Bold"
                        />
                        <IconButton
                          size="xs"
                          icon={<em>I</em>}
                          onClick={() => editor.chain().focus().toggleItalic().run()}
                          bg={editor.isActive('italic') ? 'blue.100' : 'transparent'}
                          color={editor.isActive('italic') ? 'blue.600' : 'gray.700'}
                          _hover={{ bg: editor.isActive('italic') ? 'blue.200' : 'gray.100' }}
                          aria-label="Italic"
                        />
                        <IconButton
                          size="xs"
                          icon={<u>U</u>}
                          onClick={() => editor.chain().focus().toggleUnderline().run()}
                          bg={editor.isActive('underline') ? 'blue.100' : 'transparent'}
                          color={editor.isActive('underline') ? 'blue.600' : 'gray.700'}
                          _hover={{ bg: editor.isActive('underline') ? 'blue.200' : 'gray.100' }}
                          aria-label="Underline"
                        />
                        <IconButton
                          size="xs"
                          icon={<s>S</s>}
                          onClick={() => editor.chain().focus().toggleStrike().run()}
                          bg={editor.isActive('strike') ? 'blue.100' : 'transparent'}
                          color={editor.isActive('strike') ? 'blue.600' : 'gray.700'}
                          _hover={{ bg: editor.isActive('strike') ? 'blue.200' : 'gray.100' }}
                          aria-label="Strikethrough"
                        />
                        <Select
                          size="xs"
                          w="70px"
                          bg="white"
                          color="gray.800"
                          borderColor="gray.300"
                          _hover={{ bg: 'gray.50' }}
                          onChange={(e) => editor.chain().focus().setFontSize(e.target.value).run()}
                          value={editor.getAttributes('textStyle').fontSize || '1rem'}
                        >
                          <option value="0.5rem">8</option>
                          <option value="0.625rem">10</option>
                          <option value="0.75rem">12</option>
                          <option value="0.875rem">14</option>
                          <option value="1rem">16</option>
                          <option value="1.125rem">18</option>
                          <option value="1.25rem">20</option>
                          <option value="1.5rem">24</option>
                          <option value="1.75rem">28</option>
                          <option value="2rem">32</option>
                        </Select>

                        <Box w="1px" h="20px" bg="gray.300" />

                        {/* Link Controls */}
                        {showLinkInput ? (
                          <HStack spacing={1}>
                            <Input
                              size="xs"
                              w="150px"
                              bg="white"
                              color="gray.800"
                              borderColor="gray.300"
                              placeholder="Enter URL"
                              value={linkUrl}
                              onChange={(e) => setLinkUrl(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  if (linkUrl) {
                                    editor.chain().focus().setLink({ href: linkUrl }).run();
                                  }
                                  setShowLinkInput(false);
                                  setLinkUrl('');
                                }
                                if (e.key === 'Escape') {
                                  setShowLinkInput(false);
                                  setLinkUrl('');
                                }
                              }}
                              autoFocus
                            />
                            <IconButton
                              size="xs"
                              icon={<SmallCloseIcon />}
                              onClick={() => {
                                setShowLinkInput(false);
                                setLinkUrl('');
                              }}
                              bg="transparent"
                              color="gray.700"
                              _hover={{ bg: 'gray.100' }}
                              aria-label="Cancel"
                            />
                          </HStack>
                        ) : (
                          <IconButton
                            size="xs"
                            icon={<HiOutlineLink />}
                            onClick={() => {
                              if (editor.isActive('link')) {
                                editor.chain().focus().unsetLink().run();
                              } else {
                                const previousUrl = editor.getAttributes('link').href;
                                setLinkUrl(previousUrl || '');
                                setShowLinkInput(true);
                              }
                            }}
                            bg={editor.isActive('link') ? 'blue.100' : 'transparent'}
                            color={editor.isActive('link') ? 'blue.600' : 'gray.700'}
                            _hover={{ bg: editor.isActive('link') ? 'blue.200' : 'gray.100' }}
                            aria-label="Link"
                          />
                        )}

                        <Box w="1px" h="20px" bg="gray.300" />

                        {/* Clear Formatting */}
                        <IconButton
                          size="xs"
                          icon={<MdFormatColorReset />}
                          onClick={() => {
                            editor.chain().focus().clearNodes().unsetAllMarks().run();
                          }}
                          bg="transparent"
                          color="gray.700"
                          _hover={{ bg: 'gray.100' }}
                          aria-label="Clear Formatting"
                        />
                      </HStack>
                    </BubbleMenu>
                  )}
                </div>
              </div>
            </Box>

            {isDrawerOpen && (
              <>
                <Box
                  w="4px"
                  h="100%"
                  bg="gray.300"
                  cursor="col-resize"
                  _hover={{ bg: "blue.400" }}
                  transition="background-color 0.2s"
                  onMouseDown={handleMouseDown}
                  userSelect="none"
                />
                <Box
                  w={`${panelWidth}%`}
                  pt={2}
                  px={2}
                  pb={2}
                  bg="white"
                  display="flex"
                  flexDirection="column"
                  overflow="hidden"
                  zIndex={10}
                  borderLeft="1px solid"
                  borderColor="gray.300"
                >
                  <HStack justify="space-between" mb={2} flexShrink={0}>
                    <Heading size="sm">Codified Data</Heading>
                    <HStack spacing={1}>
                      <Tooltip label="Zoom Out JSON">
                        <IconButton
                          icon={<HiOutlineMinus />}
                          size="xs"
                          onClick={() => setJsonZoom((prev) => Math.max(prev - 0.1, 0.5))}
                        />
                      </Tooltip>
                      <Text fontSize="xs" minW="40px" textAlign="center">
                        {Math.round(jsonZoom * 100)}%
                      </Text>
                      <Tooltip label="Zoom In JSON">
                        <IconButton
                          icon={<HiOutlinePlus />}
                          size="xs"
                          onClick={() => setJsonZoom((prev) => Math.min(prev + 0.1, 2.0))}
                        />
                      </Tooltip>
                      <IconButton icon={<HiOutlineArrowNarrowLeft />} aria-label="Close Codified" onClick={() => setDrawerOpen(false)} size="sm" />
                    </HStack>
                  </HStack>

                  <Box
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    p={2}
                    mb={2}
                    flexShrink={0}
                    bg="gray.50"
                  >
                    <HStack justify="space-between" mb={0}>
                      <Text fontSize="xs" fontWeight="semibold">Codify Specifications</Text>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<HiOutlinePlus />}
                          size="xs"
                          aria-label="Add specification"
                          variant="outline"
                        />
                        <MenuList maxH="300px" overflowY="auto">
                          {availableSpecifications.map(spec => {
                            const specId = spec.specificationId || spec._id || spec.id;
                            const alreadyAdded = codifySpecifications.some(cs => {
                              const csId = cs.specificationId || cs._id || cs.id;
                              return csId === specId;
                            });

                            return (
                              <MenuItem
                                key={spec.specificationId}
                                fontSize="sm"
                                isDisabled={alreadyAdded}
                                onClick={async () => {
                                  try {
                                    if (debugMode) console.log('[SPEC ADD] Menu item clicked');
                                    if (debugMode) console.log('[SPEC ADD] Selected spec:', spec);

                                    // Add specification - include both 'id' and 'specificationId' for compatibility
                                    const normalizedSpecId = spec.specificationId || spec._id || spec.id;
                                    const newSpec = {
                                      id: normalizedSpecId,
                                      specificationId: normalizedSpecId,
                                      name: spec.name,
                                      ownerPartyId: spec.ownerPartyId,
                                      codifyCollection: spec.codifyCollection,
                                      dataStandard: spec.dataStandard,
                                      dataType: spec.dataType,
                                      modelProvider: spec.modelProvider,
                                      model: spec.model,
                                    };

                                    console.log('[SPEC ADD] New spec object:', newSpec);

                                    // Fetch definitions for this specification first
                                    try {
                                      const defRes = await fetch(`https://codify.poc.mrc.ai/api/definitions?collection=${newSpec.codifyCollection}`);
                                      if (defRes.ok) {
                                        const defs = await defRes.json();
                                        const defsArray = Array.isArray(defs) ? defs : [];
                                        console.log('[SPEC ADD] Loaded definitions for new spec:', newSpec.name, 'count:', defsArray.length);

                                        // IMPORTANT: Add spec to codifySpecifications FIRST
                                        // This prevents the useEffect from overwriting our manually-set definitions
                                        setCodifySpecifications(prev => {
                                          console.log('[SPEC ADD] Adding spec to list, previous count:', prev.length);
                                          const updated = [...prev, newSpec];
                                          console.log('[SPEC ADD] Updated specs count:', updated.length);
                                          return updated;
                                        });

                                        // Update specificationDefinitions with the new spec's definitions
                                        // Do this AFTER adding to codifySpecifications to preserve existing specs' definitions
                                        setSpecificationDefinitions(prev => {
                                          const newMap = new Map(prev);
                                          console.log('[SPEC ADD] Current definitions map size:', prev.size);
                                          console.log('[SPEC ADD] Setting definitions for spec ID:', newSpec.id);
                                          newMap.set(newSpec.id, defsArray);
                                          console.log('[SPEC ADD] New definitions map size:', newMap.size);
                                          return newMap;
                                        });

                                        // Wait for refs to update, then initialize entries
                                        setTimeout(() => {
                                          console.log('[SPEC ADD] Initializing entries for:', newSpec.name);
                                          initializeSpecificationEntries(newSpec);
                                        }, 100);
                                      }
                                    } catch (err) {
                                      console.error('[SPEC ADD] Failed to load definitions:', err);
                                    }
                                  } catch (error) {
                                    console.error('[SPEC ADD] Error:', error);
                                  }
                                }}
                              >
                                {spec.name} ({spec.codifyCollection})
                              </MenuItem>
                            );
                          })}
                        </MenuList>
                      </Menu>
                    </HStack>
                  </Box>

                  <Box flex="1" overflowY="auto" overflowX="hidden">
                    <Tabs size="sm" variant="enclosed">
                      <TabList>
                        {codifySpecifications.map((spec, index) => {
                          const specId = spec.specificationId || spec._id || spec.id;
                          return (
                            <Tab key={specId}>{index + 1}</Tab>
                          );
                        })}
                      </TabList>
                      <TabPanels>
                        {/* Specification Tabs */}
                        {codifySpecifications.map(spec => {
                          // Normalize specificationId (support id, _id, and specificationId fields)
                          const specId = spec.specificationId || spec._id || spec.id;
                          const specCodifications = specificationCodifications.get(specId);

                          // Debug logging
                          if (debugMode) console.log('[TAB RENDER] Rendering tab for spec:', {
                            specName: spec.name,
                            specId: specId,
                            specificationId: spec.specificationId,
                            _id: spec._id,
                            codificationId: spec.codificationId,
                            hasCodifications: !!specCodifications,
                            codificationsSize: specCodifications?.size || 0,
                            allMapKeys: Array.from(specificationCodifications.keys()),
                          });
                          if (debugMode) {
                            if (debugMode) console.log('[TAB RENDER] Detailed spec info');
                          }

                          // Get document order from editor
                          const documentOrder = [];
                          if (editor) {
                            editor.state.doc.descendants((node) => {
                              if (node.type.name === "tableRow" && node.attrs?.id) {
                                documentOrder.push(node.attrs.id);
                              }
                            });
                          }

                          // Create rows from codifications
                          const specRows = specCodifications ? Array.from(specCodifications.entries()).map(([rowId, data]) => ({
                            id: rowId,
                            type: data.headingType || 'Unknown',
                            state: data.state || 'pending',
                            output: data.output || data.data || null,
                          })) : [];

                          // Sort rows by document order
                          specRows.sort((a, b) => {
                            const indexA = documentOrder.indexOf(a.id);
                            const indexB = documentOrder.indexOf(b.id);

                            // If row not in document, put at end
                            if (indexA === -1) return 1;
                            if (indexB === -1) return -1;

                            return indexA - indexB;
                          });

                          return (
                            <TabPanel key={specId} p={0} pt={2}>
                              <HStack justify="space-between" mb={2} px={2}>
                                <Text fontSize="sm" fontWeight="bold" color="gray.700">{spec.name}</Text>
                                <HStack spacing={1}>
                                  <Button
                                    size="xs"
                                    colorScheme="blue"
                                    variant="ghost"
                                    onClick={() => codifyAllForSpecification(spec)}
                                  >
                                    Codify All
                                  </Button>
                                  <IconButton
                                    icon={<HiOutlineTrash />}
                                    size="xs"
                                    variant="ghost"
                                    aria-label="Remove specification"
                                    onClick={() => {
                                      if (window.confirm(`Are you sure you want to remove "${spec.name}"?`)) {
                                        setCodifySpecifications(prev => prev.filter(s => {
                                          const sId = s.specificationId || s._id || s.id;
                                          return sId !== specId;
                                        }));
                                      }
                                    }}
                                    _hover={{ color: "red.500", bg: "transparent" }}
                                  />
                                </HStack>
                              </HStack>
                              {specRows.length === 0 ? (
                                <Text fontSize="sm" color="gray.500" p={4} textAlign="center">
                                  No codifications yet
                                </Text>
                              ) : (
                                <Accordion allowMultiple>
                                  {specRows.map(row => (
                                    <AccordionItem key={row.id} border="1px solid" borderColor="gray.200" borderRadius="md" mb={2}>
                                      <AccordionButton>
                                        <Box flex="1" textAlign="left">
                                          <HStack justify="space-between">
                                            <Text fontWeight="semibold" fontSize="sm">{row.type}</Text>
                                            <Text fontSize="xs" color={
                                              row.state === "running" ? "blue.600" :
                                              row.state === "changed" ? "orange.600" :
                                              row.state === "error" ? "red.600" :
                                              row.state === "codified" ? "green.600" :
                                              row.state === "no_definition" ? "yellow.700" : "gray.600"
                                            }>
                                              {row.state === "no_definition" ? "No Definition" : row.state}
                                            </Text>
                                          </HStack>
                                        </Box>
                                        <AccordionIcon />
                                      </AccordionButton>
                                      <AccordionPanel pb={2} fontSize="xs">
                                        {row.output ? (
                                          (() => {
                                            try {
                                              const parsed = JSON.parse(row.output);
                                              return (
                                                <ReactJson
                                                  src={parsed}
                                                  theme="rjv-default"
                                                  collapsed={1}
                                                  displayDataTypes={false}
                                                  displayObjectSize={false}
                                                  enableClipboard={true}
                                                  name={false}
                                                  indentWidth={2}
                                                  style={{ 
                                                    fontSize: `${11 * jsonZoom}px`, 
                                                    backgroundColor: "white" 
                                                  }}
                                                />
                                              );
                                            } catch {
                                              return <Text fontSize="xs" color="gray.600">{row.output}</Text>;
                                            }
                                          })()
                                        ) : (
                                          <Text fontSize="xs" color="gray.500">No output yet</Text>
                                        )}
                                      </AccordionPanel>
                                    </AccordionItem>
                                  ))}
                                </Accordion>
                              )}
                            </TabPanel>
                          );
                        })}
                      </TabPanels>
                    </Tabs>
                  </Box>
                </Box>
              </>
            )}
          </HStack>

          <Box p={2} borderTop="1px" borderColor="gray.300" backgroundColor="whiteAlpha.900" flexShrink={0}>
            <HStack spacing={4} justify="center">
              <Tooltip label="Zoom Out">
                <IconButton icon={<HiOutlineMinus />} size="sm" onClick={() => setZoom((prev) => Math.max(prev - 0.1, 0.5))} />
              </Tooltip>
              <Slider aria-label="Zoom Slider" value={zoom * 100} min={50} max={200} onChange={(val) => setZoom(val / 100)} width="200px">
                <SliderTrack><SliderFilledTrack /></SliderTrack>
                <SliderThumb />
              </Slider>
              <Text fontSize="sm" minW="50px" textAlign="center">{Math.round(zoom * 100)}%</Text>
              <Tooltip label="Zoom In">
                <IconButton icon={<HiOutlinePlus />} size="sm" onClick={() => setZoom((prev) => Math.min(prev + 0.1, 2))} />
              </Tooltip>
            </HStack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
